import { CONTENT_TYPE, EVENTS, PAGE } from "../../app/constants";
import { useCart } from "../../app/context/Cart/CartContext";
import { useConfig } from "../../app/context/Config/configContext";
import { useLoginModalContext } from "../../app/context/LoginModal";
import { useSoundGlobal } from "../../app/context/Sound/SoundProvider";
import { getUserCoinPackages } from "../../app/helpers/coinPackages";
import {
  getCoinName,
  getLocalisedText,
  replaceCoinWord,
} from "../../app/helpers/helper";
import useAnalytics from "../../app/hooks/useAnalytics";
import useElementOnScreen from "../../app/hooks/useElementOnScreen";
import useLikedVideo from "../../app/hooks/useLikedVideo";
import useSound from "../../app/hooks/useSound";
import AppStoreBadgeIcon from "../../assets/icons/AppStoreBadgeIcon";
import CTALock from "../../assets/icons/CTALock";
import CoinIcon from "../../assets/icons/CoinIcon";
import GooglePlayBadgeIcon from "../../assets/icons/GooglePlayBadgeIcon";
import LockIcon from "../../assets/icons/LockIcon";
import PauseIcon from "../../assets/icons/PauseIcon";
import PlayIcon from "../../assets/icons/PlayIcon";
import SeriesCoinModalIcon from "../../assets/icons/SeriesCoinModalIcon";
import SeriesIcon from "../../assets/icons/SeriesIcon";
import SeriesIcons from "../../assets/icons/SeriesIcons";
import SoundWave from "../../assets/icons/SoundWave";
import VerifiedProfile from "../../assets/icons/VerifiedProfile";
import ViewIcon from "../../assets/icons/ViewIcon";
import DefaultProfileImage from "../../assets/img/Profile.png";
import { Recommendations } from "../Recommendations/Recommendations";
import CustomModal from "../common/CustomModal/CustomModal";
import DynamicCoinPackIcon from "../common/DynamicCoinPackIcon";
import LoaderDark from "../common/LoaderDark/LoaderDark";
import Report from "../common/Report";
import CloseIcon from "./Icons/Close";
import CommentIcon from "./Icons/Comment";
import ShopIcon from "./Icons/DisLike";
import LikeIcon from "./Icons/Like";
import Mute from "./Icons/Mute";
import OptionsIcon from "./Icons/Options";
import ReportIcon from "./Icons/Report";
import RepostIcon from "./Icons/RepostIcon";
import ShareIcon from "./Icons/Share";
import UnLikeIcon from "./Icons/UnLike";
import Unmute from "./Icons/Unmute";
import Comments from "./components/Comments";
import VerticalPlayerProducts from "./components/Products";
import RepostModal from "./components/Repost/Modal";
import Share from "./components/Share";
import {
  addOptimisticUnlockId,
  convertKToNumeric,
  formatLargeNumber,
  getOptimisticUnlockIds,
  parseDescription,
} from "./helpers";
import { Menu, Transition } from "@headlessui/react";
import gluedin from "gluedin-shorts-js";
import { has } from "lodash";
import debounce from "lodash.debounce";
import { off } from "process";
import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  Fragment,
  useMemo,
  useLayoutEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

const activityTimelineModule = new gluedin.GluedInActivityTimeline();
const authModule = new gluedin.GluedInAuthModule();
const userModule = new gluedin.GluedInUserModule();
const SvodModule = new gluedin.GluedInSvodModule();
const metaCache = new Map<string, any>();
const seriesCache = new Map<string, any>();
const seriesInflight = new Map<string, Promise<any>>();
let coinBalanceCache: number | null = null;
let coinBalanceInflight: Promise<number | null> | null = null;

const extractCoinBalance = (response: any) => {
  const data = response?.data ?? response;
  const balance = Number(data?.result?.balance);
  return Number.isFinite(balance) ? balance : null;
};

const extractWatchAdConsumption = (response: any) => {
  const data = response?.data ?? response;
  const watchAdCount = Number(
    data?.result?.watch_ad ??
      data?.result?.watchAd ??
      data?.watch_ad ??
      data?.watchAd,
  );
  if (!Number.isFinite(watchAdCount) || watchAdCount < 0) return 0;
  return watchAdCount;
};

const getCoinBalance = async () => {
  if (coinBalanceCache !== null) return coinBalanceCache;
  if (coinBalanceInflight) return coinBalanceInflight;
  coinBalanceInflight = userModule
    .getUserBalance()
    .then((response: any) => {
      const balance = extractCoinBalance(response);
      if (balance !== null) {
        coinBalanceCache = balance;
      }
      return balance;
    })
    .catch((error: any) => {
      console.error(error);
      return null;
    })
    .finally(() => {
      coinBalanceInflight = null;
    });
  return coinBalanceInflight;
};

const PlayPauseButtons = ({
  className,
  handlePlayAndPause,
  isPlaying,
  showControls,
}: any) => (
  <button
    className={className}
    onClick={handlePlayAndPause}
    style={{
      opacity: showControls || !isPlaying ? 1 : 0,
    }}
  >
    {isPlaying ? <PlayIcon /> : <PauseIcon />}
  </button>
);

const VideoItem = forwardRef(
  (
    {
      video,
      setVideos,
      setCurrentVideoIndex,
      suppressIndexSyncRef,
      allVideoRefs,
      totalEpisodesCount,
      seriesDefinition,
      onEpisodeUnlock,
      likedByMe,
      userMeta,
    }: any,
    ref: any,
  ) => {
    const {
      indexNo,
      id,
      url,
      playing: isPlaying,
      //   muted: isMuted,
      thumbnailUrl,
      user,
      title,
      description = "",
      taggedUsers,
      commentCount,
      likeCount,
      shareCount,
      products = [],
      repost = false,
      repostCount,
      soundId,
      soundThumbnail,
      soundName,
      videoId,
      series,
      labels,
    } = video;
    const displayTitle = getLocalisedText(video, "title") || title;
    const displayDescription =
      getLocalisedText(video, "description") || description;
    const templateType = (
      video?.templateType || "VERTICAL_VIDEO"
    ).toUpperCase();
    const isPost = templateType === "POST";
    const mediaType = (video?.primaryMedia?.type || "").toLowerCase();
    const primaryContentType = (
      video?.contentType ||
      mediaType ||
      ""
    ).toLowerCase();
    const originalLabelKey =
      primaryContentType === "image"
        ? "label-original-image"
        : "label-original-video";
    const isImageContent = primaryContentType === "image";
    const isVideoPost = isPost && primaryContentType === "video";
    const isImagePost = isPost && isImageContent;
    const isLockedPlaceholder = Boolean(
      video?.isLockedPlaceholder ||
        video?.isPlaceholder ||
        video?.isStaticPlaceholder,
    );
    const showMediaControls = !isImageContent && !isLockedPlaceholder;
    const mediaUrls = Array.isArray(video?.primaryMedia?.urls)
      ? video.primaryMedia.urls
      : [];
    const preferredHlsUrl =
      primaryContentType === "video"
        ? mediaUrls.find(
            (mediaUrl: string) =>
              typeof mediaUrl === "string" &&
              mediaUrl.toLowerCase().includes(".m3u8"),
          ) || ""
        : "";
    const mediaSrc = preferredHlsUrl || mediaUrls[0] || url;
    const posterSrc = thumbnailUrl || mediaSrc;
    const effectiveUrl = isImageContent ? "" : mediaSrc || url;
    const apiAspectRatio = (() => {
      const width = Number(video?.primaryMedia?.width ?? video?.width);
      const height = Number(video?.primaryMedia?.height ?? video?.height);
      if (
        Number.isFinite(width) &&
        width > 0 &&
        Number.isFinite(height) &&
        height > 0
      ) {
        return width / height;
      }
      return null;
    })();
    const isPortraitNineBySixteenVideo =
      isVideoPost &&
      Number.isFinite(apiAspectRatio) &&
      Math.abs((apiAspectRatio as number) - 9 / 16) < 0.01;
    const baseAspectRatio = (() => {
      if (isVideoPost && apiAspectRatio) {
        return apiAspectRatio;
      }
      if (isImagePost) {
        // Prefer explicit aspectRatio for images; otherwise default square
        if (Number.isFinite(video?.aspectRatio) && video?.aspectRatio > 0) {
          return video.aspectRatio;
        }
        return 1;
      }

      if (Number.isFinite(video?.aspectRatio) && video?.aspectRatio > 0) {
        return video.aspectRatio;
      }

      if (
        Number.isFinite(video?.aspectBucket?.ratio) &&
        video?.aspectBucket?.ratio > 0
      ) {
        return video.aspectBucket.ratio;
      }

      return 4 / 5;
    })();
    const [dynamicAspectRatio, setDynamicAspectRatio] =
      useState(baseAspectRatio);
    const postImageSnapRatio = 4 / 5;
    const postImageSnapTolerance = 0.055;
    const normalizedImageAspectRatio =
      isImagePost &&
      Number.isFinite(dynamicAspectRatio) &&
      Math.abs(dynamicAspectRatio - postImageSnapRatio) <=
        postImageSnapTolerance
        ? postImageSnapRatio
        : dynamicAspectRatio;
    const isPortraitNineBySixteenImage =
      isImagePost &&
      Number.isFinite(dynamicAspectRatio) &&
      Math.abs(dynamicAspectRatio - 9 / 16) < 0.01;
    const hasSquarePostFrame =
      isPost && (isPortraitNineBySixteenVideo || isPortraitNineBySixteenImage);
    const playerViewportHeight = "calc(100dvh - 60px)";
    const postWidth = "calc((100dvh - 60px) * 0.5625)";
    const postAspectRatio = hasSquarePostFrame ? 1 : normalizedImageAspectRatio;
    const safePostAspectRatio =
      Number.isFinite(postAspectRatio) && postAspectRatio > 0
        ? postAspectRatio
        : 4 / 5;
    const postLayoutMaxAspectRatio = 4 / 5;
    const isTwoByThreePostRatio =
      isPost &&
      !hasSquarePostFrame &&
      Math.abs(safePostAspectRatio - 2 / 3) < 0.03;
    // Force known overflow-prone 2:3-like posts to 4:5.
    const effectivePostAspectRatio = isTwoByThreePostRatio
      ? postLayoutMaxAspectRatio
      : safePostAspectRatio;
    const rawPostHeight = `calc(${postWidth} / ${effectivePostAspectRatio.toFixed(
      4,
    )})`;
    // Defensive cap: if computed post height gets too tall, clamp to 4:5 layout max.
    const postLayoutMaxHeight = `calc(${postWidth} / ${postLayoutMaxAspectRatio.toFixed(
      4,
    )})`;
    const postHeight = `min(${rawPostHeight}, ${postLayoutMaxHeight}, ${playerViewportHeight})`;
    const playerStyle = undefined; // keep container styling unchanged
    const mediaStyle = isPost
      ? ({
          width: postWidth,
          height: postHeight,
          maxHeight: `min(${postLayoutMaxHeight}, ${playerViewportHeight})`,
          ...(hasSquarePostFrame ? { objectFit: "contain" } : {}),
        } as React.CSSProperties)
      : undefined;
    const showPostMediaFrame = hasSquarePostFrame;
    const postFrameStyle = showPostMediaFrame
      ? ({
          width: postWidth,
          height: postHeight,
          maxHeight: `min(${postLayoutMaxHeight}, ${playerViewportHeight})`,
        } as React.CSSProperties)
      : undefined;
    const postVideoStyle = showPostMediaFrame
      ? ({
          width: "100%",
          height: "100%",
          objectFit: "contain",
        } as React.CSSProperties)
      : mediaStyle;
    const { muted, toggleMute, setMuted } = useSoundGlobal();
    const { userId, fullName, profileImageUrl, userName } = user ?? {};
    const [showControls, setShowControls] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [showSeries, setShowSeries] = useState(false);
    const [showShopping, setShowShopping] = useState(false);
    const [selectedSize, setSelectedSize] = useState<"regular" | "large">(
      "regular",
    );
    const [shoppingProduct, setShoppingProduct] = useState<any>(null);
    const [showShare, setShowShare] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const [shouldShowMore, setShouldShowMore] = useState(false);
    const [descMaxHeight, setDescMaxHeight] = useState(40);
    const [descTitleHeight, setDescTitleHeight] = useState(0);
    const [descClampOverride, setDescClampOverride] = useState<number | null>(
      null,
    );
    const [isFollowing, setIsFollowing] = useState(false);
    const [isLikingVideo, setIsLikingVideo] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showCommentReportModal, setShowCommentReportModal] = useState(false);
    const [showLike, setShowLike] = useState(false);
    const [showRepost, setShowRepost] = useState(false);
    const [commentPostId, setCommentPostId] = useState("");
    const initialCommentCount = Number.isFinite(Number(commentCount))
      ? Number(commentCount)
      : 0;
    const [videoCommentCount, setVideoCommentCount] =
      useState(initialCommentCount);
    const safeCommentCount = Number.isFinite(Number(videoCommentCount))
      ? Number(videoCommentCount)
      : 0;
    const { t } = useTranslation();
    const rawSequenceMessage = t("label-unlock-sequence");
    const sequenceUnlockMessage =
      rawSequenceMessage === "label-unlock-sequence"
        ? "This episode can be unlocked only after unlocking the previous episode."
        : rawSequenceMessage;
    const showMoreLabel = t("label-show-more");
    const showLessLabel = t("label-show-less");
    const resolvePlayerLabel = (key: string, fallback: string) => {
      const value = t(key);
      return value === key ? fallback : value;
    };
    const { setShowLoginModal } = useLoginModalContext();
    const navigate = useNavigate();
    const { trackEvent } = useAnalytics();
    const { addToCart } = useCart();
    const parsedFullDescription = parseDescription(
      displayDescription,
      taggedUsers,
    );
    const [showVideoError, setShowVideoError] = useState(false);
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const ownContentUserId = video?.userId ?? userId;
    const isOwnContent =
      !!userData?.userId &&
      !!ownContentUserId &&
      userData.userId === ownContentUserId;
    const statusValue = String(
      video?.status ?? video?.contentStatus ?? "",
    ).toLowerCase();
    const isPending = statusValue === "pending";
    const isActionDisabled = statusValue !== "active";
    const blockIfDisabled = (event?: React.SyntheticEvent) => {
      if (!isActionDisabled) return false;
      event?.preventDefault();
      event?.stopPropagation();
      return true;
    };
    const videoRef = ref;
    const playerItemRef = useRef<HTMLDivElement>(null);
    const videoControls = useRef<HTMLDivElement>(null);
    const userInfoRef = useRef<HTMLDivElement | null>(null);
    const repostRef = useRef<HTMLDivElement | null>(null);
    const topInfoRef = useRef<HTMLDivElement | null>(null);
    const descRef = useRef<HTMLDivElement | null>(null);
    const postDescRef = useRef<HTMLDivElement | null>(null);
    const productsRef = useRef<HTMLDivElement | null>(null);
    const ctaRef = useRef<HTMLDivElement | null>(null);
    const progressRef = useRef<HTMLDivElement | null>(null);
    const options = {
      root: document.getElementsByClassName("player_wrapper")[0],
      rootMargin: "0px",
      threshold: 0.66, // ⬅️ was 0.5; higher reduces flicker
    };
    const [videoErrorMessage, setVideoErrorMessage] = useState("");
    const isVisible = useElementOnScreen(options, videoRef);
    const isVisibleRef = useRef(isVisible);
    const { appConfig } = useConfig();
    const [userMetaData, setUserMetaData]: any = useState(userMeta);
    // const [seriesData, setSeriesData]: any = useState([]);
    const seriesData: any = useRef([]);
    const lastWatchedEpisode: any = useRef(null);
    const [showCTA, setShowCTA] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const [showCoinModal, setShowCoinModal] = useState(false);
    const [showSeriesUnlock, setShowSeriesUnlock] = useState(false);
    const [unlockNotice, setUnlockNotice] = useState<string | null>(null);
    const [showSequenceUnlockModal, setShowSequenceUnlockModal] =
      useState(false);
    const resumePlaybackRef = useRef(false);
    const episodes = seriesData?.current?.episodes || [];
    const totalEpisodes = episodes?.length;
    const promoEpisodesCount = Array.isArray(episodes)
      ? episodes.filter((episode: any) => episode?.promo === true).length
      : 0;
    const episodesCount = Math.max(totalEpisodes - promoEpisodesCount, 0);
    const [searchParams] = useSearchParams();
    const contentType = searchParams.get("contentType");
    const userPersona = localStorage.getItem("userPersona");
    const [seriesNotAvailable, setSeriesNotAvailable] = useState(false);
    const contentRoute = localStorage.getItem("contentRoute");
    const [progress, setProgress] = useState(0);
    const [videoIsPlaying, setVideoIsPlaying] = useState(isPlaying);
    const [videoPlaybackDuration, setVideoPlaybackDuration] = useState(0);
    const hasTrackedReplay = useRef<boolean>(false);
    const hasTrackedView = useRef<boolean>(false);
    const hasTrackedEnded = useRef<boolean>(false);
    const tolerance = 0.3;
    const replayResetThreshold = 0.5;
    // const [lastWatchedEpisode, setLastWatchedEpisode]: any = useState(null);

    const [showRecommendations, setShowRecommendations] = useState(false);
    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [offset, setOffset] = useState(1);
    const limit = 10;
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingPage, setIsLoadingPage] = useState(false);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [navLock, setNavLock] = useState(false);
    const playbackErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const stepRef = useRef<number>(0); // exact delta between items
    const isSeriesContext = contentType === "series" || !!series;
    const paymentConfiguration = seriesDefinition?.paymentConfiguration || {};
    const ctaConfiguration =
      seriesData?.current?.ctaConfiguration ||
      seriesDefinition?.ctaConfiguration ||
      {};
    const paymentCoinCost = Number(paymentConfiguration?.coins);
    const initialCoinBalance = userData?.coinBalance ?? userData?.coins ?? 0;
    const [coinBalance, setCoinBalance] = useState(initialCoinBalance);
    const [watchAdConsumedCount, setWatchAdConsumedCount] = useState(0);
    const [coinPackages, setCoinPackages] = useState<any[]>([]);
    const [hasResolvedCoinUnlockMeta, setHasResolvedCoinUnlockMeta] =
      useState(false);
    const [activeUnlockFreeSection, setActiveUnlockFreeSection] = useState<
      "engagement" | "ads"
    >("engagement");
    const [activeSeriesUnlockView, setActiveSeriesUnlockView] = useState<
      "purchase" | "free"
    >("purchase");
    const closeSwapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const episodeCoinCost = Number.isFinite(paymentCoinCost)
      ? paymentCoinCost
      : series?.coinCost ?? series?.coinsRequired ?? series?.coins ?? 10;
    const extractUnlockId = (entry: any) =>
      entry?.videoId ??
      entry?.topicId ??
      entry?.episodeId ??
      entry?.id ??
      entry?._id ??
      entry;

    const getEpisodeKeyCandidates = (entry: any) => {
      if (!entry) return [];
      const candidates = [
        entry?.videoId,
        entry?.topicId,
        entry?.episodeId,
        entry?.id,
        entry?._id,
      ]
        .map((value) =>
          value !== undefined && value !== null ? String(value) : "",
        )
        .filter(Boolean);

      return Array.from(new Set(candidates));
    };

    const unlockedEpisodeIds = useMemo(() => {
      const rawList =
        seriesDefinition?.unlockEpisodes ||
        seriesDefinition?.unlockedEpisodes ||
        seriesDefinition?.unlockEpisodeIds ||
        [];
      if (!Array.isArray(rawList)) return new Set<string>();
      const ids = rawList
        .map((entry: any) => extractUnlockId(entry))
        .map((value: any) =>
          value !== undefined && value !== null ? String(value) : "",
        )
        .filter(Boolean);
      const optimisticIds = getOptimisticUnlockIds(
        series?.seriesId || seriesDefinition?.seriesId,
      );
      if (optimisticIds.length) {
        optimisticIds.forEach((id) => {
          if (id) ids.push(id);
        });
      }
      return new Set(ids);
    }, [seriesDefinition, series?.seriesId, seriesDefinition?.seriesId]);

    const isEpisodeUnlocked = (episode: any) => {
      const episodeId = extractUnlockId(episode);
      if (episodeId === undefined || episodeId === null) return false;
      return unlockedEpisodeIds.has(String(episodeId));
    };

    const markEpisodeUnlocked = (episode: any) => {
      const episodeId = extractUnlockId(episode);
      if (episodeId === undefined || episodeId === null) return;
      addOptimisticUnlockId(
        series?.seriesId || seriesDefinition?.seriesId,
        String(episodeId),
      );
    };

    const episodeKeysMatch = (left: any, right: any) => {
      const leftCandidates = getEpisodeKeyCandidates(left);
      const rightCandidates = getEpisodeKeyCandidates(right);
      if (!leftCandidates.length || !rightCandidates.length) return false;
      const rightSet = new Set(rightCandidates);
      return leftCandidates.some((candidate) => rightSet.has(candidate));
    };

    const findSeriesDefinitionEpisode = (episode: any) => {
      if (!episode) return null;
      const definitionEpisodes = seriesDefinition?.episodes;
      if (!Array.isArray(definitionEpisodes)) return null;
      return (
        definitionEpisodes.find((entry: any) =>
          episodeKeysMatch(entry, episode),
        ) || null
      );
    };

    const isPromoEpisode = (episode: any) => {
      if (!episode) return false;
      if (episode?.promo === true) return true;
      const definitionEpisode = findSeriesDefinitionEpisode(episode);
      return definitionEpisode?.promo === true;
    };

    const episodeDisplayOrderMap = (() => {
      const sourceEpisodes =
        Array.isArray(seriesDefinition?.episodes) &&
        seriesDefinition.episodes.length
          ? seriesDefinition.episodes
          : Array.isArray(seriesData?.current?.episodes)
          ? seriesData.current.episodes
          : [];
      const orderMap = new Map<string, number>();
      let displayCounter = 0;

      sourceEpisodes.forEach((item: any) => {
        if (isPromoEpisode(item)) return;
        const keys = getEpisodeKeyCandidates(item);
        if (!keys.length) return;
        const hasKnownKey = keys.some((key) => orderMap.has(key));
        if (hasKnownKey) return;
        displayCounter += 1;
        keys.forEach((key) => {
          if (!orderMap.has(key)) {
            orderMap.set(key, displayCounter);
          }
        });
      });

      return orderMap;
    })();

    const getEpisodeDisplayNumber = (episode: any) => {
      if (!episode || isPromoEpisode(episode)) return null;
      const candidateKeys = getEpisodeKeyCandidates(episode);
      for (const candidate of candidateKeys) {
        if (episodeDisplayOrderMap.has(candidate)) {
          return episodeDisplayOrderMap.get(candidate) ?? null;
        }
      }

      const allEpisodes = Array.isArray(seriesData?.current?.episodes)
        ? seriesData.current.episodes
        : [];
      if (!allEpisodes.length) {
        const seriesEpisode = Number(episode?.series?.episode);
        if (Number.isFinite(seriesEpisode) && seriesEpisode > 0) {
          return seriesEpisode;
        }
        return null;
      }

      let visibleCounter = 0;
      for (const item of allEpisodes) {
        if (!isPromoEpisode(item)) {
          visibleCounter += 1;
        }
        if (episodeKeysMatch(item, episode)) {
          return isPromoEpisode(item) ? null : visibleCounter;
        }
      }
      return null;
    };

    const isEpisodeLockedForUser = (episode: any) => {
      if (!episode) return false;
      if (isPromoEpisode(episode)) return false;
      let isPaid = false;
      if (
        userPersona &&
        userPersona === seriesData?.current?.persona?.[userPersona]?.key
      ) {
        isPaid = false;
      } else if (
        (episode?.paid &&
          userPersona &&
          userPersona !== seriesData?.current?.persona?.[userPersona]?.key) ||
        (episode?.paid && !userPersona)
      ) {
        isPaid = true;
      }
      if (isEpisodeUnlocked(episode)) {
        isPaid = false;
      }
      return isPaid;
    };
    const isCurrentEpisodeLockedForUser = isEpisodeLockedForUser(video);

    const hasLockedPreviousEpisode = (episode: any) => {
      if (!Array.isArray(filteredEpisodes) || filteredEpisodes.length === 0) {
        return false;
      }
      const targetIndex = filteredEpisodes.findIndex((entry: any) =>
        episodeKeysMatch(entry, episode),
      );
      if (targetIndex <= 0) return false;
      for (let i = 0; i < targetIndex; i += 1) {
        if (isEpisodeLockedForUser(filteredEpisodes[i])) {
          return true;
        }
      }
      return false;
    };

    const getFirstLockedEpisodeTarget = (episode: any) => {
      if (!Array.isArray(filteredEpisodes) || filteredEpisodes.length === 0) {
        return null;
      }

      const targetIndex = filteredEpisodes.findIndex((entry: any) =>
        episodeKeysMatch(entry, episode),
      );
      const searchEndIndex =
        targetIndex >= 0 ? targetIndex : filteredEpisodes.length - 1;

      for (let i = 0; i <= searchEndIndex; i += 1) {
        const candidateEpisode = filteredEpisodes[i];
        if (!isEpisodeLockedForUser(candidateEpisode)) {
          continue;
        }
        const candidateLabel =
          getEpisodeDisplayNumber(candidateEpisode) ??
          filteredEpisodes
            .slice(0, i + 1)
            .filter((entry: any) => !isPromoEpisode(entry)).length;
        return {
          episode: candidateEpisode,
          episodeLabel: candidateLabel,
        };
      }

      return null;
    };

    const isCurrentEpisodeUnlocked = isEpisodeUnlocked(video);
    const isCurrentEpisodePromo = isPromoEpisode(video);

    const isSeriesLocked =
      contentType === "series" &&
      !isCurrentEpisodePromo &&
      !isCurrentEpisodeUnlocked &&
      isCurrentEpisodeLockedForUser;
    const hasPaymentConfig =
      paymentConfiguration && Object.keys(paymentConfiguration).length > 0;
    const isSubscriptionLock = paymentConfiguration?.subscription === true;
    const isCoinLock =
      paymentConfiguration?.enabled === true &&
      Number.isFinite(Number(paymentConfiguration?.coins));
    const isSubscriptionPlatformEnabled =
      appConfig?.subscriptionsEnabled !== false;
    const isCoinPlatformEnabled =
      appConfig?.appEconomy?.coinSettings?.coinPlatformEnabled !== false;
    const subscriptionProvider = String(
      appConfig?.subscriptionProvider ??
        appConfig?.subscription?.provider ??
        appConfig?.subscriptions?.provider ??
        "",
    ).toUpperCase();
    const subscriptionsEnabled = Boolean(
      appConfig?.subscriptionsEnabled ??
        appConfig?.subscription?.enabled ??
        appConfig?.subscriptions?.enabled,
    );
    const canUnlockBySubscription =
      isSubscriptionLock && isSubscriptionPlatformEnabled;
    const canUnlockByCoins = isCoinLock && isCoinPlatformEnabled;
    const isEarnFromAdsEnabledForCoinFlow =
      appConfig?.appEconomy?.coinSettings?.earnFromAds?.enabled !== false;
    const isEarnFromEngagementEnabledForCoinFlow =
      appConfig?.appEconomy?.coinSettings?.earnFromEngagement?.enabled !==
      false;
    const hasCoinPackages =
      Array.isArray(coinPackages) && coinPackages.length > 0;
    const isCoinAcquisitionPathDisabled =
      canUnlockByCoins &&
      !isEarnFromAdsEnabledForCoinFlow &&
      !isEarnFromEngagementEnabledForCoinFlow;
    const isCoinUnlockAvailabilityPending =
      isSeriesLocked &&
      canUnlockByCoins &&
      !hasResolvedCoinUnlockMeta &&
      ((isCoinAcquisitionPathDisabled && !canUnlockBySubscription) ||
        subscriptionProvider === "PERSONA_BASED");
    const shouldHideCoinUnlockUiWhileLoading =
      isCoinUnlockAvailabilityPending ||
      (isSeriesLocked && canUnlockByCoins && !hasResolvedCoinUnlockMeta);
    const shouldForceNoPurchaseOptionsForCoinUnlock =
      isSeriesLocked &&
      isCoinAcquisitionPathDisabled &&
      hasResolvedCoinUnlockMeta &&
      !hasCoinPackages &&
      !canUnlockBySubscription;
    const shouldHideCoinUnlockPanelForUnavailableCoinFlow =
      isCoinPlatformEnabled &&
      canUnlockByCoins &&
      !isEarnFromAdsEnabledForCoinFlow &&
      !isEarnFromEngagementEnabledForCoinFlow &&
      (!hasResolvedCoinUnlockMeta || !hasCoinPackages);
    const showNoPurchaseOptionsLock =
      isSeriesLocked &&
      ((!canUnlockBySubscription && !canUnlockByCoins) ||
        shouldForceNoPurchaseOptionsForCoinUnlock);
    const lockUiType = shouldHideCoinUnlockUiWhileLoading
      ? null
      : showNoPurchaseOptionsLock
      ? "no-purchase"
      : canUnlockByCoins
      ? "coin"
      : canUnlockBySubscription || !hasPaymentConfig
      ? "subscription"
      : null;
    const showSubscriptionLock =
      isSeriesLocked && lockUiType === "subscription";
    const showCoinUnlock = isSeriesLocked && lockUiType === "coin";
    const descTextRef = useRef<HTMLSpanElement | null>(null);
    const descMeasureRef = useRef<HTMLSpanElement | null>(null);
    const getDescLineHeight = () => {
      const el = descTextRef.current;
      if (!el) return 22;
      const lineHeight = Number.parseFloat(
        window.getComputedStyle(el).lineHeight || "0",
      );
      return Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 22;
    };
    const [collapsedDescription, setCollapsedDescription] = useState<
      string | null
    >(null);
    const [descWidth, setDescWidth] = useState(0);

    useEffect(() => {
      isVisibleRef.current = isVisible;
    }, [isVisible]);

    useEffect(() => {
      if (!isSeriesLocked || (!showCoinUnlock && !showSubscriptionLock)) {
        setShowSeriesUnlock(false);
        return;
      }
      setShowSeriesUnlock(false);
      const timer = setTimeout(() => {
        setShowSeriesUnlock(true);
      }, 120);

      return () => clearTimeout(timer);
    }, [isSeriesLocked, showCoinUnlock, showSubscriptionLock, videoId]);

    useEffect(() => {
      setCoinBalance(initialCoinBalance);
    }, [initialCoinBalance]);

    useEffect(() => {
      setUnlockNotice(null);
      setShowSequenceUnlockModal(false);
      resumePlaybackRef.current = false;
    }, [videoId]);

    const updateLocalCoinBalance = (nextBalance: number) => {
      const safeBalance = Number.isFinite(nextBalance) ? nextBalance : 0;
      setCoinBalance(safeBalance);
      coinBalanceCache = safeBalance;
      try {
        const stored = localStorage.getItem("userData");
        if (!stored) return;
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object") {
          parsed.coinBalance = safeBalance;
          parsed.coins = safeBalance;
          localStorage.setItem("userData", JSON.stringify(parsed));
        }
      } catch (error) {
        console.log(error);
      }
    };

    const pauseCurrentVideoForUnlock = () => {
      const current = videoRef?.current as HTMLVideoElement | undefined;
      if (!current) {
        resumePlaybackRef.current = false;
        return;
      }
      if (!current.paused) {
        resumePlaybackRef.current = true;
        current.pause();
        return;
      }
      resumePlaybackRef.current = false;
    };

    const resumeCurrentVideoIfNeeded = () => {
      if (!resumePlaybackRef.current) return;
      resumePlaybackRef.current = false;
      const current = videoRef?.current as HTMLVideoElement | undefined;
      if (!current) return;
      current.play().catch(() => {});
    };

    const openSequenceUnlockNotice = () => {
      setUnlockNotice(sequenceUnlockMessage);
      setShowSequenceUnlockModal(true);
      pauseCurrentVideoForUnlock();
    };

    const closeSequenceUnlockNotice = () => {
      setUnlockNotice(null);
      setShowSequenceUnlockModal(false);
      resumeCurrentVideoIfNeeded();
    };

    useEffect(() => {
      if (!isSeriesLocked || !canUnlockByCoins) {
        setHasResolvedCoinUnlockMeta(false);
        return;
      }
      let isActive = true;
      setHasResolvedCoinUnlockMeta(false);
      const fetchCoinUnlockMeta = async () => {
        const [balanceResult, packagesResult, watchAdResult] =
          await Promise.allSettled([
            userData?.userId ? getCoinBalance() : Promise.resolve(null),
            getUserCoinPackages(),
            isEarnFromAdsEnabledForCoinFlow && userData?.userId
              ? userModule.getUserCoinConsumptionLimit()
              : Promise.resolve(null),
          ]);
        if (!isActive) return;

        if (balanceResult.status === "fulfilled") {
          const balance = balanceResult.value;
          if (typeof balance === "number") {
            setCoinBalance(balance);
          }
        } else if (userData?.userId) {
          console.error(balanceResult.reason);
        }

        if (packagesResult.status === "fulfilled") {
          setCoinPackages(packagesResult.value);
        } else {
          console.error(packagesResult.reason);
          setCoinPackages([]);
        }

        if (watchAdResult.status === "fulfilled") {
          setWatchAdConsumedCount(
            extractWatchAdConsumption(watchAdResult.value),
          );
        } else if (isEarnFromAdsEnabledForCoinFlow && userData?.userId) {
          console.error(watchAdResult.reason);
          setWatchAdConsumedCount(0);
        }

        setHasResolvedCoinUnlockMeta(true);
      };
      fetchCoinUnlockMeta();
      return () => {
        isActive = false;
      };
    }, [
      canUnlockByCoins,
      isSeriesLocked,
      isEarnFromAdsEnabledForCoinFlow,
      userData?.userId,
    ]);

    useEffect(() => {
      if (!isPost || !isImagePost) return;
      if (!mediaSrc) {
        setDynamicAspectRatio(1); // fallback square
        return;
      }

      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          const ratio = img.naturalWidth / img.naturalHeight;
          if (Number.isFinite(ratio) && ratio > 0) {
            setDynamicAspectRatio(Number(ratio.toFixed(4)));
          }
        }
      };
      img.onerror = () => {
        setDynamicAspectRatio(1); // fallback square on error
      };
      img.src = mediaSrc;

      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }, [isPost, isImagePost, mediaSrc]);

    useEffect(() => {
      setShowMore(false);
      setShowShopping(false);
      setShoppingProduct(null);
      setSelectedSize("regular");
      errorShownRef.current = false;
      setShowVideoError(false);
      setVideoErrorMessage("");
      if (playbackErrorTimerRef.current) {
        clearTimeout(playbackErrorTimerRef.current);
        playbackErrorTimerRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.classList.remove("video-player-radius");
        videoControls.current?.classList.remove("video-player-radius");
      }
    }, [videoId, templateType, effectiveUrl]);

    useEffect(
      () => () => {
        if (playbackErrorTimerRef.current) {
          clearTimeout(playbackErrorTimerRef.current);
          playbackErrorTimerRef.current = null;
        }
      },
      [],
    );

    useEffect(() => {
      if (!showMore) {
        // only the post container is scrollable in your code
        if (isPost && postDescRef.current) {
          postDescRef.current.scrollTop = 0;
        }
      }
    }, [showMore, isPost, videoId]);

    useLayoutEffect(() => {
      const el = descRef.current;
      if (!el) return;

      const update = () => {
        const nextWidth = el.clientWidth || 0;
        if (nextWidth && Math.abs(nextWidth - descWidth) > 1) {
          setDescWidth(nextWidth);
        }
      };

      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      window.addEventListener("resize", update);

      return () => {
        ro.disconnect();
        window.removeEventListener("resize", update);
      };
    }, [descWidth, videoId]);

    useEffect(() => {
      const computeClamp = () => {
        const h = window.innerHeight;
        if (h <= 640) {
          setDescClampOverride(32); // ~1.5 lines
        } else if (h <= 720) {
          setDescClampOverride(36); // ~2 lines
        } else if (h <= 840) {
          setDescClampOverride(44); // ~2.5 lines
        } else {
          setDescClampOverride(null);
        }
      };

      computeClamp();
      window.addEventListener("resize", computeClamp);
      return () => window.removeEventListener("resize", computeClamp);
    }, []);

    useEffect(() => {
      const el = videoRef?.current;
      if (!el || isImageContent) return;

      const onPlay = () => {
        setVideoIsPlaying(true); // <- your requirement
        setShowVideoError(false);
        setVideoErrorMessage("");
        errorShownRef.current = false;
        updateVideos("playing", true); // keep parent store in sync
      };
      const onPlaying = onPlay;

      const onPause = () => {
        setVideoIsPlaying(false);
        updateVideos("playing", false);
      };
      const onEnded = onPause;

      el.addEventListener("play", onPlay);
      el.addEventListener("playing", onPlaying);
      el.addEventListener("pause", onPause);
      el.addEventListener("ended", onEnded);

      return () => {
        el.removeEventListener("play", onPlay);
        el.removeEventListener("playing", onPlaying);
        el.removeEventListener("pause", onPause);
        el.removeEventListener("ended", onEnded);
      };
    }, [videoRef, isImageContent]);

    // prevents immediate pause right after play due to quick visibility flips
    const playLockRef = useRef(false);

    const tryPlay = async () => {
      const el = videoRef?.current;
      if (!el) return;
      try {
        await el.play();
      } catch (err: any) {
        // Autoplay fallback: mute and retry once
        if (err?.name === "NotAllowedError") {
          el.muted = true;
          updateVideos("muted", true);
          try {
            await el.play();
          } catch {}
        }
      } finally {
        // short hysteresis to avoid pause right after play
        playLockRef.current = true;
        setTimeout(() => (playLockRef.current = false), 300);
      }
    };

    useEffect(() => {
      if (!videoIsPlaying && progress === 0) {
        // Briefly remove transition when restarting
        const timeout = setTimeout(() => {
          setVideoIsPlaying(true);
        }, 100); // Delay helps prevent reverse animation
        return () => clearTimeout(timeout);
      }
    }, [videoIsPlaying, progress]);

    // Video Progess Bar Start //
    useEffect(() => {
      const videoElement = videoRef?.current;

      if (!videoElement || isImageContent) return;

      const updateProgress = () => {
        if (videoElement && videoElement?.duration) {
          const progressValue =
            (videoElement?.currentTime / videoElement?.duration) * 100;
          setProgress(progressValue);
          // reset any stale error once we have valid duration/progress
          if (videoElement?.currentTime > 0 && !videoElement.error) {
            errorShownRef.current = false;
          }
        }
      };

      const handlePlay = () => setVideoIsPlaying(true);
      const handlePause = () => setVideoIsPlaying(false);
      const handleEnded = () => {
        setVideoIsPlaying(false);
        setProgress(100); // Set to 100% when the video ends
      };

      videoElement.addEventListener("timeupdate", updateProgress);
      videoElement.addEventListener("play", handlePlay);
      videoElement.addEventListener("pause", handlePause);
      videoElement.addEventListener("ended", handleEnded);

      return () => {
        videoElement.removeEventListener("timeupdate", updateProgress);
        videoElement.removeEventListener("play", handlePlay);
        videoElement.removeEventListener("pause", handlePause);
        videoElement.removeEventListener("ended", handleEnded);
      };
    }, [isImageContent]);
    // Video Progess Bar End //

    const [isUnlockingEpisode, setIsUnlockingEpisode] = useState(false);
    const [isAutoUnlockingVisibleEpisode, setIsAutoUnlockingVisibleEpisode] =
      useState(false);
    const [shouldRenderUnlockLoader, setShouldRenderUnlockLoader] =
      useState(false);
    const [isUnlockLoaderVisible, setIsUnlockLoaderVisible] = useState(false);
    const unlockLoaderFadeTimerRef = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);
    const unlockLoaderRafRef = useRef<number | null>(null);

    const getSafeSeriesOffset = (episodeLabel?: number) => {
      if (typeof episodeLabel !== "number") return null;
      const safeIndex = Math.max(episodeLabel === 0 ? 1 : episodeLabel - 3, 1);
      return Number.isFinite(safeIndex) ? safeIndex : null;
    };

    const resolveEpisodeId = (episode: any) =>
      episode?.id || episode?.videoId || episode?._id || video?.videoId || id;

    const buildSeriesHref = (episodeId: string, offset?: number | null) => {
      const offsetParam = typeof offset === "number" ? `&offset=${offset}` : "";
      if (contentRoute && contentRoute === PAGE.DISCOVER) {
        return `/${PAGE.DISCOVER}/series/${series?.seriesId}?onScreenElementId=${episodeId}${offsetParam}&contentType=${CONTENT_TYPE.SERIES}`;
      }
      return `/series/${series?.seriesId}?onScreenElementId=${episodeId}${offsetParam}&contentType=${CONTENT_TYPE.SERIES}`;
    };

    const handlePaidEpisodeClick = async (
      episode: any,
      episodeLabel?: number,
    ) => {
      const episodeId = resolveEpisodeId(episode);
      const offset = getSafeSeriesOffset(episodeLabel);
      const href = buildSeriesHref(String(episodeId), offset);
      window.location.href = href;
    };

    const handleFreeEpisodeClick = async (item: any, episodeLabel: any) => {
      const episodeId = resolveEpisodeId(item);
      const offset = getSafeSeriesOffset(episodeLabel);
      const href = buildSeriesHref(String(episodeId), offset);
      window.location.href = href;
    };

    const handleUnlockEpisodeWithCoins = async (
      item: any,
      episodeLabel: number,
      options: {
        shouldNavigate?: boolean;
        onUnlocked?: () => void;
      } = {},
    ) => {
      const { shouldNavigate = true, onUnlocked } = options;
      if (!canUnlockByCoins) {
        return;
      }
      if (isUnlockingEpisode) return;
      if (!Number.isFinite(episodeCoinCost)) {
        if (shouldNavigate) {
          await handlePaidEpisodeClick(item, episodeLabel);
        }
        return;
      }

      if (!userData?.userId) {
        setShowLoginModal(true);
        return;
      }

      if (hasLockedPreviousEpisode(item)) {
        openSequenceUnlockNotice();
        return;
      }

      let resolvedBalance = coinBalance;
      if (coinBalanceCache === null || resolvedBalance < episodeCoinCost) {
        const latestBalance = await getCoinBalance();
        if (typeof latestBalance === "number") {
          resolvedBalance = latestBalance;
          updateLocalCoinBalance(latestBalance);
        }
      }

      if (resolvedBalance < episodeCoinCost) {
        if (shouldNavigate) {
          await handlePaidEpisodeClick(item, episodeLabel);
        }
        return;
      }

      const previousBalance = resolvedBalance;
      const nextBalance = Math.max(previousBalance - episodeCoinCost, 0);
      let hasMarkedUnlock = false;
      setIsUnlockingEpisode(true);
      updateLocalCoinBalance(nextBalance);

      try {
        const assetId = item?.videoId || item?.id;
        await activityTimelineModule.activityTimelineEventLog({
          assetId,
          eventType: "UNBLOCK_EPISODE",
          seriesId: series?.seriesId || seriesDefinition?.seriesId,
        });

        markEpisodeUnlocked(item);
        hasMarkedUnlock = true;
        setIsUnlockingEpisode(false);

        if (typeof onEpisodeUnlock === "function") {
          await onEpisodeUnlock();
        } else {
          await handleFetchSeries(series?.seriesId);
        }

        if (shouldNavigate) {
          await handleFreeEpisodeClick(item, episodeLabel);
        } else if (typeof onUnlocked === "function") {
          onUnlocked();
        } else if (isVisibleRef.current) {
          tryPlay();
        }
      } catch (error) {
        console.log(error);
        updateLocalCoinBalance(previousBalance);
      } finally {
        if (!hasMarkedUnlock) {
          setIsUnlockingEpisode(false);
        }
      }
    };

    useEffect(() => {
      autoUnlockAttemptedRef.current = false;
      setIsAutoUnlockingVisibleEpisode(false);
    }, [videoId]);

    useEffect(() => {
      if (!isVisible) return;
      if (isUnlockingEpisode) return;
      if (autoUnlockAttemptedRef.current) return;
      if (!series?.seriesId) return;
      if (!canUnlockByCoins) return;
      if (!isEpisodeLockedForUser(video)) return;

      autoUnlockAttemptedRef.current = true;
      let isActive = true;
      const knownBalance =
        coinBalanceCache !== null ? coinBalanceCache : Number(coinBalance);
      const hasEnoughKnownBalance =
        Number.isFinite(knownBalance) && knownBalance >= episodeCoinCost;
      const canRunSilentAutoUnlock =
        Boolean(userData?.userId) &&
        Number.isFinite(episodeCoinCost) &&
        !hasLockedPreviousEpisode(video) &&
        hasEnoughKnownBalance;
      setIsAutoUnlockingVisibleEpisode(canRunSilentAutoUnlock);
      const episodeLabel = getEpisodeDisplayNumber(video) ?? 0;
      void handleUnlockEpisodeWithCoins(video, episodeLabel, {
        shouldNavigate: false,
        onUnlocked: () => {
          if (isVisibleRef.current) {
            void tryPlay();
          }
        },
      }).finally(() => {
        if (isActive) {
          setIsAutoUnlockingVisibleEpisode(false);
        }
      });

      return () => {
        isActive = false;
      };
    }, [
      isVisible,
      showCoinUnlock,
      isSeriesLocked,
      isUnlockingEpisode,
      episodeCoinCost,
      videoId,
      video,
      seriesData?.current?.episodes,
      seriesDefinition,
    ]);

    const closePopup = () => {
      setShowPopup(false);
      setPopupData(null);
    };

    useEffect(() => {
      if (appConfig?.series?.watchFullCTA?.enabled) {
        const timer = setTimeout(() => {
          setShowCTA(true);
        }, appConfig?.series.watchFullCTA.delay * 1000);

        return () => clearTimeout(timer);
      }
    }, [series]);

    useEffect(() => {
      if (userMeta !== undefined) {
        setUserMetaData(userMeta);
        return;
      }
      setUserMetaData(undefined);
      async function fetchMetaData(userId: string) {
        if (metaCache.has(userId)) {
          setUserMetaData(metaCache.get(userId));
          return;
        }
        try {
          var UserModuleObj = new gluedin.GluedInFeedModule();
          var userMetaData = await UserModuleObj.getMetadata({
            type: "storyOwner",
            ids: [userId],
          });
          if (userMetaData.status === 200) {
            const userResponse = userMetaData.data.result;
            const data = userResponse?.storyOwner[0] || [];
            metaCache.set(userId, data);
            setUserMetaData(data);
          }
        } catch (error) {
          console.log(error);
        }
      }
      if (userId) {
        fetchMetaData(userId);
      }
    }, [userId, userMeta]);

    const shouldFetchLike = typeof likedByMe !== "boolean";
    const { isVideoLiked: fetchedIsVideoLiked } = useLikedVideo(
      videoId,
      shouldFetchLike,
    );
    const [isVideoLiked, setIsVideoLiked] = useState(Boolean(likedByMe));

    useEffect(() => {
      if (typeof likedByMe === "boolean") {
        setIsVideoLiked(likedByMe);
        return;
      }
      if (shouldFetchLike) {
        setIsVideoLiked(fetchedIsVideoLiked);
      }
    }, [fetchedIsVideoLiked, likedByMe, shouldFetchLike]);

    const updateVideos = (type: string, status: boolean) => {
      setVideos((videos: any) => {
        const newVideos = videos.map((video: any) => {
          if (video.id === id) {
            video[type] = status;
          }
          return video;
        });
        return newVideos;
      });
    };

    const errorShownRef = useRef(false);
    const showErrorPopup = (message: any) => {
      if (isImageContent) return;
      if (!isVisibleRef.current) return;
      if (errorShownRef.current) return;
      const el = videoRef?.current as HTMLVideoElement | null;
      if (!el?.error) return;
      const hasSrc = !!(el?.src || effectiveUrl);
      const isReady = el && el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA;
      const isPlaying =
        el &&
        !el.paused &&
        !el.ended &&
        el.currentTime > 0 &&
        el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;

      // Suppress false positives when player is actually ready/playing
      if (isPlaying || (isReady && !el?.error)) return;
      if (!hasSrc) return;

      errorShownRef.current = true;
      setVideoErrorMessage(message);
      setShowVideoError(true);
    };

    // Put inside the component (above the useEffect is fine)
    const autoAdvanceToNext = () => {
      const nextIndex = (indexNo ?? 0) + 1;

      // 1) Update index in parent state (both Feed and Series pass this)
      if (typeof setCurrentVideoIndex === "function") {
        setCurrentVideoIndex((prev: number) => Math.max(prev + 1, nextIndex));
      }

      // 2) Scroll next card into view
      // First try the refs passed from Feed (index.tsx)
      const nextRef = allVideoRefs?.current?.[nextIndex]?.current;
      if (nextRef) {
        nextRef.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      // Fallback: query the DOM (works in Series page too)
      const cards = document.getElementsByClassName("player_item_wrapper");
      const el = cards?.[nextIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    useEffect(() => {
      const videoElement = videoRef?.current;
      if (!videoElement || isImageContent) return;

      const isLastSeriesEpisode = () => {
        const epNum =
          typeof video?.series?.episode === "number"
            ? video.series.episode
            : null;

        return (
          isSeriesContext &&
          epNum !== null &&
          epNum > 0 &&
          Number.isFinite(episodesCount) &&
          episodesCount > 0 &&
          epNum === episodesCount
        );
      };

      const handleEndedAdvance = () => {
        if (!isSeriesContext) return;
        if (isLastSeriesEpisode()) return;
        autoAdvanceToNext();
      };

      const handleTimeUpdate = () => {
        const playbackTime = Number.isFinite(videoElement.currentTime)
          ? parseFloat(videoElement.currentTime.toFixed(2))
          : 0;
        const playbackDuration = Number.isFinite(videoElement.duration)
          ? parseFloat(videoElement.duration.toFixed(2))
          : 0;

        setVideoPlaybackDuration(playbackDuration);

        // Track view at 3 seconds
        if (!hasTrackedView.current && playbackTime >= 3) {
          hasTrackedView.current = true;
          trackEvent({ event: EVENTS.CONTENT_VIEW, content: video });

          setTimeout(() => {
            handleFetchSeries(series?.seriesId);
          }, 1000);
        }

        // End handling (keep your existing 'last-episode only' check if you added it)
        const nearEnd =
          Number.isFinite(playbackDuration) &&
          playbackDuration > 0 &&
          playbackTime >= playbackDuration - tolerance;

        const isLastEpisode = isLastSeriesEpisode();
        if (!hasTrackedEnded.current && nearEnd) {
          hasTrackedEnded.current = true;

          // Fire ended only for last episode in series
          if (isLastEpisode) {
            trackEvent({ event: EVENTS.CONTENT_ENDED, content: video });
            videoElement.pause();
            setShowRecommendations(true);
          }
        }

        // Allow re-trigger if user scrubs back far from end
        if (hasTrackedEnded.current && playbackTime < playbackDuration - 2) {
          hasTrackedEnded.current = false;
        }

        // Reset tracking if restarted before 3s
        if (playbackTime < 3 && hasTrackedReplay.current) {
          hasTrackedReplay.current = false;
          hasTrackedView.current = false;
          hasTrackedEnded.current = false;
        }
      };

      const handlePlaybackError = () => {
        const message = t("label-video-error-playback");
        console.error(message);
        if (playbackErrorTimerRef.current) {
          clearTimeout(playbackErrorTimerRef.current);
        }
        playbackErrorTimerRef.current = setTimeout(() => {
          showErrorPopup(message);
          playbackErrorTimerRef.current = null;
        }, 450);
      };

      videoElement.addEventListener("timeupdate", handleTimeUpdate);
      videoElement.addEventListener("ended", handleEndedAdvance);
      videoElement.addEventListener("error", handlePlaybackError);

      return () => {
        if (playbackErrorTimerRef.current) {
          clearTimeout(playbackErrorTimerRef.current);
          playbackErrorTimerRef.current = null;
        }
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);
        videoElement.removeEventListener("ended", handleEndedAdvance);
        videoElement.removeEventListener("error", handlePlaybackError);
      };
      // deps intentionally exclude objects that cause re-init; url change still re-runs
    }, [
      effectiveUrl,
      videoRef,
      series?.seriesId,
      contentType,
      episodesCount,
      isImageContent,
    ]);

    const toggleReportModal = () => {
      setShowReportModal((prev) => !prev);
    };

    const toggleCommentReportModal = () => {
      setShowCommentReportModal((prev) => !prev);
    };

    const updateUrl = useRef(
      debounce((videoId: string) => {
        const url = new URL(window.location.href);
        const searchParams = url.searchParams;

        if (searchParams.get("onScreenElementId") !== videoId) {
          searchParams.set("onScreenElementId", videoId);
          navigate(`${url.pathname}?${searchParams.toString()}`, {
            replace: true,
          });
        }
      }, 1000),
    ).current;

    useEffect(() => {
      const el = videoRef.current;
      if (!el) return;

      if (isVisible) {
        if (!suppressIndexSyncRef?.current) {
          setCurrentVideoIndex(indexNo);
        }
        tryPlay(); // robust play with autoplay fallback
        updateVideos("playing", true);
        setCurrentVideoIndex(indexNo);
        if (!isImageContent) {
          tryPlay(); // robust play with autoplay fallback
          updateVideos("playing", true);
        } else {
          updateVideos("playing", false);
        }
        updateUrl(videoId);
      } else {
        // avoid immediate pause right after a successful play
        if (!playLockRef.current) {
          el.pause();
          updateVideos("playing", false);
        }
      }
      // Only respond to visibility flips
    }, [isVisible, isImageContent]);

    useEffect(() => {
      if (!isVisible) return;
      if (isLockedPlaceholder) return;
      if (isSeriesLocked) return;
      if (!effectiveUrl) return;
      tryPlay();
    }, [isVisible, isLockedPlaceholder, isSeriesLocked, effectiveUrl]);

    useEffect(() => {
      if (Object.keys(userData).length !== 0) {
        let followingList: any = localStorage.getItem("following");
        if (followingList) {
          followingList = JSON.parse(followingList);
          if (followingList.includes(userId)) {
            setIsFollowing(true);
          } else {
            setIsFollowing(false);
          }
        }
      }
    }, [setMuted]);

    const isLoggedin = async () => {
      const accessToken =
        await new gluedin.GluedInAuthModule().getAccessToken();
      if (accessToken) {
        return true;
      } else {
        return false;
      }
    };

    const handleStoryViewClick = async (
      event: React.MouseEvent<HTMLAnchorElement>,
      href: string,
    ) => {
      if (blockIfDisabled(event)) return;
      event.preventDefault();
      const loggedIn = await isLoggedin();
      if (!loggedIn) {
        setShowLoginModal(true);
        return;
      }
      navigate(href);
    };

    const handlePlayAndPause = () => {
      if (isActionDisabled) return;
      const el = videoRef.current;
      if (!el) return;
      if (el.paused) {
        el.play().catch((err: any) => {
          if (err?.name === "NotAllowedError") {
            el.muted = true;
            updateVideos("muted", true);
            el.play().catch(() => {});
          }
        });
      } else {
        el.pause();
      }
    };

    // When this item mounts or url changes, ensure element reflects global muted
    useEffect(() => {
      if (videoRef?.current) {
        ref.current.muted = muted;
      }
    }, [ref, muted]);

    const handleMuteUnmute = () => {
      if (isActionDisabled) return;
      // Flip global state; all mounted videos will reflect instantly
      toggleMute();
    };

    const handleMouseOver = () => {
      setShowControls(true);
    };

    const handleMouseOut = () => {
      setShowControls(false);
    };

    const handleFollowEvent = async (
      event: any,
      userId: any,
      followStatus: boolean,
    ) => {
      if (isActionDisabled) return;
      try {
        const isUserLoggedIn: any = await isLoggedin();
        if (!isUserLoggedIn) {
          setShowLoginModal(true);
          return;
        }
        let accessToken = await authModule.getAccessToken();
        if (
          accessToken === "" ||
          accessToken === undefined ||
          accessToken === null
        ) {
          setShowLoginModal(true);
          return;
        }
        let followingList: any = localStorage.getItem("following");
        followingList = JSON.parse(followingList);
        var formData = {
          followingId: userId,
          isFollow: followStatus,
        };
        userModule.followUser(formData);
        if (followStatus) {
          followingList.push(userId);
          localStorage.setItem("following", JSON.stringify(followingList));
          setIsFollowing(true);
        } else {
          const newFollowing = followingList.filter((id: any) => id !== userId);
          localStorage.setItem("following", JSON.stringify(newFollowing));
          setIsFollowing(false);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const handleLikeEvent = async (e: any, videoId: string) => {
      if (isActionDisabled) return;
      e.preventDefault();
      setIsLikingVideo(true);
      let selfTarget = e.currentTarget;
      let likeElement: any = document.getElementById("like-count-" + videoId);
      const videoLikedByMe = selfTarget.classList.contains("active");
      const isUserLoggedIn = await isLoggedin();
      if (!isUserLoggedIn) {
        setShowLoginModal(true);
        setIsLikingVideo(false);
        return;
      }
      if (isVideoLiked) {
        let unlikeObj = {
          assetId: videoId,
          metakeys: {},
          event: "unlike",
          ...(series && {
            posType: "episode",
          }),
        };
        const userPostedComment =
          await activityTimelineModule.activityTimelineUnLike(unlikeObj);

        const { status } = userPostedComment ?? {};
        if (status === 200) {
          //   trackEvent({
          //     event: EVENTS.CONTENT_UNLIKE,
          //     content: video,
          //   });
          selfTarget.classList.remove("active");
          let likeNumber: any = convertKToNumeric(likeElement.innerHTML);
          likeNumber--;
          likeNumber = likeNumber >= 0 ? likeNumber : 0;
          const likeCount = formatLargeNumber(likeNumber);
          likeElement.innerHTML = likeCount;
          setIsVideoLiked(false);
          setIsLikingVideo(false);
        }
      } else {
        let likeObj = {
          assetId: videoId,
          metakeys: {},
          event: "like",
          ...(series && {
            posType: "episode",
          }),
        };
        activityTimelineModule
          .activityTimelineLike(likeObj)
          .then((response: any) => {
            const { status } = response ?? {};
            if (status === 200) {
              //   trackEvent({
              //     event: EVENTS.CONTENT_LIKE,
              //     content: video,
              //   });
              selfTarget.classList.add("active");
              let likeNumber: any = convertKToNumeric(likeElement.innerHTML);
              likeNumber++;
              let likeCount = formatLargeNumber(likeNumber);
              likeElement.innerHTML = likeCount;
              setIsVideoLiked(true);
              setIsLikingVideo(false);
            }
          });
      }
    };

    const toggleRepostModal = () => {
      setShowRepost((prev) => !prev);
    };
    const reDirectToOriginalVideo = () => {
      //   trackEvent({
      //     event: EVENTS.VIEW_IMPRESSION,
      //     content: video,
      //   });
      navigate(
        // `/content/${video.videoId}`
        `/content/${video.user.userId}?contentType=${CONTENT_TYPE.PROFILE}&clickedVideoId=${video.originalVideoId}`,
      );
    };

    const handleClickOnSound = () => {
      navigate(`/sound-track/${soundId}`);
    };

    const handleAddToCart = async (product: any) => {
      if (!product) return;
      const isUserLoggedIn = await isLoggedin();
      if (!isUserLoggedIn) {
        setShowLoginModal(true);
        return;
      }
      addToCart(product);
    };

    const parsedCollapsedDescription = collapsedDescription
      ? parseDescription(collapsedDescription, taggedUsers)
      : null;
    const descriptionContent =
      shouldShowMore && !showMore && parsedCollapsedDescription
        ? parsedCollapsedDescription
        : parsedFullDescription;

    // Convert array to string if parsedFullDescription is an array
    const videoDescription = Array.isArray(parsedFullDescription)
      ? parsedFullDescription.join(" ") // Join array elements into a single string with spaces
      : typeof parsedFullDescription === "string"
      ? parsedFullDescription
      : "";

    useLayoutEffect(() => {
      const measure = () => {
        // POST: available space is dynamic
        if (isPost) {
          const userInfoEl = userInfoRef.current;
          const postDescEl = postDescRef.current;
          const textEl = descMeasureRef.current;

          if (!userInfoEl || !postDescEl || !textEl) {
            setShouldShowMore(false);
            return;
          }

          const userInfoHeight = userInfoEl.clientHeight || 0;
          const topInfoHeight = topInfoRef.current?.offsetHeight || 0;
          const repostHeight = repostRef.current?.offsetHeight || 0;
          const productsHeight = productsRef.current?.offsetHeight || 0;
          const ctaHeight = ctaRef.current?.offsetHeight || 0;
          const progressHeight = progressRef.current?.offsetHeight || 0;

          const userInfoStyles = window.getComputedStyle(userInfoEl);
          const userInfoPadding =
            parseFloat(userInfoStyles.paddingTop || "0") +
            parseFloat(userInfoStyles.paddingBottom || "0");

          const titleEl = postDescEl.querySelector(
            ".title",
          ) as HTMLElement | null;
          const titleHeight = titleEl?.offsetHeight || 0;
          const titleStyles = titleEl ? window.getComputedStyle(titleEl) : null;
          const titleMargins = titleStyles
            ? Number.parseFloat(titleStyles.marginTop || "0") +
              Number.parseFloat(titleStyles.marginBottom || "0")
            : 0;
          const titleBlockHeight = titleHeight + titleMargins;
          const lineHeight = getDescLineHeight();

          let available =
            userInfoHeight -
            userInfoPadding -
            topInfoHeight -
            repostHeight -
            productsHeight -
            ctaHeight -
            progressHeight;

          const availableTextHeight = Math.max(
            lineHeight,
            available - titleBlockHeight,
          );
          const maxLines = Math.max(
            1,
            Math.floor(availableTextHeight / lineHeight),
          );
          const snappedTextHeight = maxLines * lineHeight;
          setDescTitleHeight(titleBlockHeight);
          setDescMaxHeight(snappedTextHeight);

          // show more if description text exceeds the available text area
          setShouldShowMore(textEl.scrollHeight > snappedTextHeight + 2);
          return;
        }

        // NON-POST: fixed clamp with screen-height override
        const clampHeight = descClampOverride ?? 40;
        setDescMaxHeight(clampHeight);

        const textEl = descMeasureRef.current;
        if (!textEl) {
          setShouldShowMore(false);
          return;
        }

        setShouldShowMore(textEl.scrollHeight > clampHeight + 2);
      };

      const raf = requestAnimationFrame(measure);

      // optional but highly recommended (fixes resize/layout changes)
      const ro = new ResizeObserver(() => measure());
      if (userInfoRef.current) ro.observe(userInfoRef.current);
      if (postDescRef.current) ro.observe(postDescRef.current);
      if (descMeasureRef.current) ro.observe(descMeasureRef.current);

      window.addEventListener("resize", measure);

      return () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
        window.removeEventListener("resize", measure);
      };
    }, [
      videoId,
      videoDescription,
      descClampOverride,
      isPost,
      products?.length,
      showCTA,
      videoPlaybackDuration,
      dynamicAspectRatio,
    ]);

    useLayoutEffect(() => {
      const fullText = (displayDescription || "").trim();
      if (!fullText) {
        if (collapsedDescription !== null) {
          setCollapsedDescription(null);
        }
        return;
      }

      const container = descRef.current;
      const textEl = descTextRef.current;
      if (!container || !textEl) return;

      const availableHeight = descMaxHeight;

      const computed = window.getComputedStyle(textEl);
      const width =
        textEl.getBoundingClientRect().width || container.clientWidth || 0;
      const measureEl = document.createElement("span");
      measureEl.style.position = "absolute";
      measureEl.style.visibility = "hidden";
      measureEl.style.pointerEvents = "none";
      measureEl.style.whiteSpace = "normal";
      measureEl.style.wordBreak = computed.wordBreak || "break-word";
      measureEl.style.width = `${width}px`;
      measureEl.style.fontFamily = computed.fontFamily;
      measureEl.style.fontSize = computed.fontSize;
      measureEl.style.fontWeight = computed.fontWeight;
      measureEl.style.lineHeight = computed.lineHeight;
      measureEl.style.letterSpacing = computed.letterSpacing;
      measureEl.style.fontStyle = computed.fontStyle;
      measureEl.style.display = "block";
      measureEl.style.padding = "0";
      measureEl.style.margin = "0";
      document.body.appendChild(measureEl);

      const words = fullText.split(/\s+/);
      const label = showMoreLabel;
      let result = "";

      for (let i = 0; i < words.length; i += 1) {
        const next = result ? `${result} ${words[i]}` : words[i];
        measureEl.textContent = `${next}… ${label}`;
        if (measureEl.scrollHeight > availableHeight) {
          break;
        }
        result = next;
      }

      if (!result && words.length > 0) {
        result = words[0];
      }

      document.body.removeChild(measureEl);

      if (result !== collapsedDescription) {
        setCollapsedDescription(result);
      }
    }, [
      shouldShowMore,
      showMore,
      displayDescription,
      descMaxHeight,
      descWidth,
      showMoreLabel,
      collapsedDescription,
    ]);

    async function handleFetchSeries(seriesId: string) {
      try {
        if (!seriesId) return;
        if (seriesCache.has(seriesId)) {
          const cached = seriesCache.get(seriesId);
          seriesData.current = cached.series;
          lastWatchedEpisode.current = cached.lastWatchedEpisode;
          return;
        }

        if (!seriesInflight.has(seriesId)) {
          const p = SvodModule.getSeriesDefinitionById({ seriesId })
            .then((res: any) => {
              if (res?.data?.success) {
                const series = res?.data?.result;
                const lastWatched = res?.data?.lastWatchedEpisode;
                seriesCache.set(seriesId, {
                  series,
                  lastWatchedEpisode: lastWatched,
                });
                return { series, lastWatched };
              }
              return null;
            })
            .finally(() => {
              seriesInflight.delete(seriesId);
            });
          seriesInflight.set(seriesId, p);
        }

        const result = await seriesInflight.get(seriesId);
        if (result) {
          seriesData.current = result.series;
          lastWatchedEpisode.current = result.lastWatchedEpisode;
        }
      } catch (error) {
        console.log("Error", error);
      }
    }

    useEffect(() => {
      handleFetchSeries(series?.seriesId);
    }, [series?.seriesId]);

    async function handleNavigateSeries() {
      if (seriesData?.current === null) {
        setSeriesNotAvailable(true);
      } else {
        navigate(
          `/series/${series?.seriesId}?contentType=${
            CONTENT_TYPE.SERIES
          }&offset=${1}`,
        );
      }
    }

    const filteredEpisodes = seriesData?.current?.episodes || [];

    const totalEpisodeCounter = (() => {
      const parsedTotalEpisodesCount = Number(totalEpisodesCount);
      if (
        Number.isFinite(parsedTotalEpisodesCount) &&
        parsedTotalEpisodesCount > 0
      ) {
        return parsedTotalEpisodesCount;
      }
      if (Number.isFinite(totalEpisodes) && totalEpisodes > 0) {
        return totalEpisodes;
      }
      return episodesCount;
    })();
    const currentEpisodeCounter = getEpisodeDisplayNumber(video);
    const isCurrentPromoEpisode = isPromoEpisode(video);

    async function handleNavigateCTA(link: string) {
      if (/^https?:\/\//i.test(link)) {
        window.location.href = link;
      } else {
        console.warn("Invalid absolute URL:", link);
      }
    }

    const removeVideo = async () => {
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.removeAttribute("src");
        const sources = video.querySelectorAll("source");
        sources.forEach((s: any) => s.remove());
        video.load();
      }
      await handleFetchSeries(series?.seriesId);
      await handlePaidEpisodeClick({
        id: videoId,
        paid: series?.paid,
        promo: series?.promo,
      });
    };

    const openLinkInNewTab = (link: string) => {
      const normalizedLink =
        link.startsWith("http://") || link.startsWith("https://")
          ? link
          : `https://${link}`;
      window.open(normalizedLink, "_blank");
    };

    const resolvePersonaCtaValue = (
      source: any,
      type: "text" | "url",
    ): string => {
      if (!source) return "";

      const pickFromEntry = (entry: any): string => {
        if (!entry) return "";
        if (typeof entry === "string") return entry;
        if (typeof entry !== "object") return "";
        if (type === "text") {
          return (
            entry?.ctaText || entry?.cta || entry?.text || entry?.title || ""
          );
        }
        return entry?.ctaUrl || entry?.url || entry?.link || "";
      };

      if (typeof source === "string") {
        return source;
      }

      if (typeof source === "object") {
        if (
          userPersona &&
          Object.prototype.hasOwnProperty.call(source, userPersona)
        ) {
          const personaValue = pickFromEntry(source[userPersona]);
          if (personaValue) return personaValue;
        }

        const firstValue = Object.values(source)[0];
        return pickFromEntry(firstValue);
      }

      return "";
    };

    const getDefaultSeriesCtaText = () => {
      const ctaObj = seriesDefinition?.cta || {};
      const userPersonaExistsInCta =
        userPersona &&
        Object.prototype.hasOwnProperty.call(ctaObj, userPersona);

      if (userPersonaExistsInCta) {
        return ctaObj[userPersona]?.cta || "";
      }

      const firstCTA = Object.values(ctaObj)[0] as { cta?: string };
      return firstCTA?.cta || "";
    };

    const getDefaultSeriesCtaUrl = () => {
      const ctaObj = seriesDefinition?.cta || {};
      const userPersonaExistsInCta =
        userPersona &&
        Object.prototype.hasOwnProperty.call(ctaObj, userPersona);

      if (userPersonaExistsInCta) {
        return ctaObj[userPersona]?.link || "";
      }

      const firstCTA = Object.values(ctaObj)[0] as { link?: string };
      return firstCTA?.link || "";
    };

    const getResolvedCtaUrl = () => {
      if (subscriptionProvider === "PERSONA_BASED") {
        return (
          resolvePersonaCtaValue(ctaConfiguration, "url") ||
          getDefaultSeriesCtaUrl()
        );
      }
      return getDefaultSeriesCtaUrl();
    };

    const getResolvedCtaText = () => {
      if (subscriptionProvider === "PERSONA_BASED") {
        return (
          resolvePersonaCtaValue(ctaConfiguration, "text") ||
          getDefaultSeriesCtaText()
        );
      }
      return getDefaultSeriesCtaText();
    };

    const handleCTARedirection = () => {
      const ctaUrl = getResolvedCtaUrl();
      if (ctaUrl) {
        openLinkInNewTab(ctaUrl);
      }
    };

    const getCTAText = () => {
      return getResolvedCtaText() || `${t("label-subscribe-now")}`;
    };

    const appDownloadUrl =
      appConfig?.appDownload?.url ||
      appConfig?.downloadApp?.url ||
      appConfig?.appDownloadUrl ||
      appConfig?.downloadAppUrl;
    const coinPackIconUrl = appConfig?.appEconomy?.coinSettings?.coinUrl;
    const showUnlockForFreeAdsOption =
      appConfig?.appEconomy?.coinSettings?.earnFromAds?.enabled !== false;
    const showEarnFromEngagementTip =
      appConfig?.appEconomy?.coinSettings?.earnFromEngagement?.enabled !==
      false;
    const showUnlockForFreeOption =
      showUnlockForFreeAdsOption || showEarnFromEngagementTip;
    const showBothUnlockForFreeModes =
      showUnlockForFreeAdsOption && showEarnFromEngagementTip;
    const shouldShowEngagementFreeSection =
      showEarnFromEngagementTip &&
      (!showBothUnlockForFreeModes || activeUnlockFreeSection === "engagement");
    const shouldShowAdsFreeSection =
      showUnlockForFreeAdsOption &&
      (!showBothUnlockForFreeModes || activeUnlockFreeSection === "ads");
    const coinsPerAd = Number(
      appConfig?.appEconomy?.coinSettings?.earnFromAds?.attributes?.asViewer
        ?.coinsPerAd,
    );
    const watchAdRewardCoins =
      Number.isFinite(coinsPerAd) && coinsPerAd >= 0
        ? coinsPerAd
        : episodeCoinCost;
    const watchAdDailyLimit = Number(
      appConfig?.appEconomy?.coinSettings?.earnFromAds?.attributes?.asViewer
        ?.dailyLimit,
    );
    const hasWatchAdDailyLimit =
      Number.isFinite(watchAdDailyLimit) && watchAdDailyLimit > 0;
    const watchedAdsToday = Math.max(0, watchAdConsumedCount);
    const remainingAdsToday = hasWatchAdDailyLimit
      ? Math.max(watchAdDailyLimit - watchedAdsToday, 0)
      : 0;
    const adsWatchedTodayLabel = hasWatchAdDailyLimit
      ? `${watchedAdsToday}/${watchAdDailyLimit} ${resolvePlayerLabel(
          "label-ads-watched-today-text",
          "Ads watched today",
        )} - ${remainingAdsToday} ${resolvePlayerLabel(
          "label-remaining",
          "remaining",
        )}`
      : `${watchedAdsToday} ${resolvePlayerLabel(
          "label-ads-watched-today-text",
          "Ads watched today",
        )}`;
    const translatedCoinsLabel = t("label-coins");
    const defaultCoinsLabel =
      translatedCoinsLabel === "label-coins" ? "Coins" : translatedCoinsLabel;
    const coinName = getCoinName(appConfig, defaultCoinsLabel);
    const withCoinName = (value: string) => replaceCoinWord(value, coinName);
    const shouldShowSeriesUnlockCta =
      subscriptionsEnabled && isSubscriptionLock;
    const shouldShowSeriesUnlockCtaForCurrentSection =
      shouldShowSeriesUnlockCta &&
      (!showBothUnlockForFreeModes || shouldShowEngagementFreeSection);
    const shouldShowSeriesUnlockDivider =
      shouldShowSeriesUnlockCtaForCurrentSection &&
      (!shouldHideCoinUnlockPanelForUnavailableCoinFlow ||
        hasCoinPackages ||
        shouldShowAdsFreeSection ||
        shouldShowEngagementFreeSection);
    const shouldUseOutlinedEarnButton =
      shouldShowSeriesUnlockCta && showUnlockForFreeOption;
    const shouldShowInAppPurchaseUnsupportedNotice =
      subscriptionProvider === "IN_APP" && subscriptionsEnabled;
    const shouldUsePersonaBasedSplitUnlockUi =
      showCoinUnlock &&
      subscriptionProvider === "PERSONA_BASED" &&
      showUnlockForFreeOption &&
      hasCoinPackages;
    const shouldUsePersonaBasedSubscriptionAdsOnlyNoPackUi =
      showCoinUnlock &&
      subscriptionProvider === "PERSONA_BASED" &&
      subscriptionsEnabled &&
      showUnlockForFreeAdsOption &&
      !showEarnFromEngagementTip &&
      hasResolvedCoinUnlockMeta &&
      !hasCoinPackages;
    const shouldUsePersonaBasedTwoStepUnlockUi =
      shouldUsePersonaBasedSplitUnlockUi ||
      shouldUsePersonaBasedSubscriptionAdsOnlyNoPackUi;
    const canSwapSeriesUnlockView =
      shouldUsePersonaBasedTwoStepUnlockUi ||
      (showCoinUnlock && showBothUnlockForFreeModes);
    const shouldShowPurchaseUnlockView =
      shouldUsePersonaBasedTwoStepUnlockUi &&
      activeSeriesUnlockView === "purchase";
    const shouldShowFreeUnlockView =
      shouldUsePersonaBasedTwoStepUnlockUi && activeSeriesUnlockView === "free";
    const shouldShowPersonaCombinedFreeView =
      showUnlockForFreeAdsOption && showEarnFromEngagementTip;
    const shouldShowPersonaEngagementOnlyView =
      !showUnlockForFreeAdsOption && showEarnFromEngagementTip;
    const shouldShowPersonaAdsOnlyView =
      showUnlockForFreeAdsOption && !showEarnFromEngagementTip;
    const watchAdCtaLabel = (() => {
      const value = t("label-watch-ad");
      return value === "label-watch-ad" ? "Watch Ad" : value;
    })();
    const buyCoinsLabel = withCoinName(
      resolvePlayerLabel("label-buy-coins", "Buy Coins"),
    );
    const unlockEpisodeTitle = resolvePlayerLabel(
      "label-unlock-this-episode-in",
      "Unlock this Episode in",
    );
    const chooseUnlockTitle = resolvePlayerLabel(
      "label-choose-how-to-unlock",
      "Episode Locked - Choose How to Unlock",
    );
    const subscriptionPlanUnlockTitle = resolvePlayerLabel(
      "label-unlock-with-subscription-plan",
      "Unlock this Episode with Subscription Plan",
    );
    const chooseUnlockDescription = withCoinName(
      resolvePlayerLabel(
        "label-choose-how-to-unlock-description",
        "You don't have enough Coins to continue. Watch a short ad to earn Coins and unlock this episode.",
      ),
    );
    const watchAdRewardLabel = (() => {
      const value = t("label-watch-ad-earn-coins", {
        coins: watchAdRewardCoins,
      });
      if (value === "label-watch-ad-earn-coins") {
        return withCoinName(`Watch an ad to earn ${watchAdRewardCoins} Coins`);
      }
      return withCoinName(value);
    })();
    const earnCoinsTipText = withCoinName(
      resolvePlayerLabel(
        "label-earn-coins-engagement-tip",
        "Play, watch, and interact - every move earns you Coins",
      ),
    );
    const subscriptionPlanCtaLabel = resolvePlayerLabel(
      "label-subscribe-now",
      "Subscribe Now",
    );
    const splitAdsWatchedTodayLabel = hasWatchAdDailyLimit
      ? `${watchedAdsToday}/${watchAdDailyLimit} ${resolvePlayerLabel(
          "label-ads-watched-today-text",
          "Ads watched today",
        )} • ${remainingAdsToday} ${resolvePlayerLabel(
          "label-remaining",
          "remaining",
        )}`
      : adsWatchedTodayLabel;
    const getVisibleCoinPackPriceLabel = (pack: any) => {
      if (typeof pack?.priceLabel === "string") {
        const trimmed = pack.priceLabel.trim();
        if (trimmed && !trimmed.includes("-")) {
          return trimmed;
        }
      }
      const parsedPrice = Number(pack?.price);
      if (Number.isFinite(parsedPrice) && parsedPrice > 0) {
        return `$${parsedPrice.toFixed(2)}`;
      }
      return "";
    };

    const handleSeriesUnlockCtaClick = () => {
      if (shouldShowInAppPurchaseUnsupportedNotice) {
        setShowCoinModal(true);
        return;
      }
      handleCTARedirection();
    };

    const handleCoinPackClick = () => {
      setShowCoinModal(true);
    };

    const handleSeriesUnlockClose = () => {
      if (!canSwapSeriesUnlockView) {
        return;
      }
      if (showCoinUnlock && shouldUsePersonaBasedTwoStepUnlockUi) {
        if (closeSwapTimerRef.current) {
          clearTimeout(closeSwapTimerRef.current);
        }
        setShowSeriesUnlock(false);
        closeSwapTimerRef.current = setTimeout(() => {
          setActiveSeriesUnlockView((prev) =>
            prev === "purchase" ? "free" : "purchase",
          );
          setShowSeriesUnlock(true);
          closeSwapTimerRef.current = null;
        }, 280);
        return;
      }
      if (showCoinUnlock && showBothUnlockForFreeModes) {
        if (closeSwapTimerRef.current) {
          clearTimeout(closeSwapTimerRef.current);
        }
        setShowSeriesUnlock(false);
        closeSwapTimerRef.current = setTimeout(() => {
          setActiveUnlockFreeSection((prev) =>
            prev === "engagement" ? "ads" : "engagement",
          );
          setShowSeriesUnlock(true);
          closeSwapTimerRef.current = null;
        }, 280);
        return;
      }
      setShowSeriesUnlock(false);
    };

    const handleWatchAdClick = () => {
      setShowCoinModal(true);
    };

    const handleEarnCoinsNavigation = () => {
      setShowSeriesUnlock(false);
      navigate("/earn-coins");
    };

    useEffect(() => {
      if (closeSwapTimerRef.current) {
        clearTimeout(closeSwapTimerRef.current);
        closeSwapTimerRef.current = null;
      }
      if (shouldUsePersonaBasedTwoStepUnlockUi) {
        setActiveSeriesUnlockView("purchase");
      }
      if (showEarnFromEngagementTip) {
        setActiveUnlockFreeSection("engagement");
        return;
      }
      if (showUnlockForFreeAdsOption) {
        setActiveUnlockFreeSection("ads");
      }
    }, [
      videoId,
      showEarnFromEngagementTip,
      showUnlockForFreeAdsOption,
      shouldUsePersonaBasedTwoStepUnlockUi,
    ]);

    useEffect(
      () => () => {
        if (closeSwapTimerRef.current) {
          clearTimeout(closeSwapTimerRef.current);
          closeSwapTimerRef.current = null;
        }
      },
      [],
    );

    // --- Scrub-to-seek support ---
    const isScrubbingRef = useRef(false);
    const wasPlayingRef = useRef(false);
    const pointerIdRef = useRef<number | null>(null);
    const rafSeekRef = useRef<number | null>(null);
    // Smooth progress + scrubbing state
    const rafId = useRef<number | null>(null);
    const [isScrubbing, setIsScrubbing] = useState(false);
    const autoUnlockAttemptedRef = useRef(false);

    const seekFromClientX = (clientX: number, barEl: HTMLDivElement) => {
      const v = videoRef.current;
      if (!v || !isFinite(v.duration) || v.duration <= 0) return;

      const rect = barEl.getBoundingClientRect();
      // Detect direction from the element itself (more accurate than document.dir)
      const isRTL = getComputedStyle(barEl).direction === "rtl";

      // In LTR:  x = clientX - rect.left
      // In RTL:  x = rect.right - clientX  (mirror horizontally)
      const rawX = isRTL ? rect.right - clientX : clientX - rect.left;

      const clampedX = Math.max(0, Math.min(rawX, rect.width));
      const ratio = rect.width ? clampedX / rect.width : 0;

      v.currentTime = ratio * v.duration;
      setProgress(ratio * 100);
    };

    const handleProgressPointerDown = (
      e: React.PointerEvent<HTMLDivElement>,
    ) => {
      if (isActionDisabled) return;
      e.preventDefault();
      e.stopPropagation();

      const barEl = e.currentTarget;
      pointerIdRef.current = e.pointerId;
      barEl.setPointerCapture(e.pointerId);

      isScrubbingRef.current = true;
      // If you prefer pausing during scrub, uncomment next line:
      // wasPlayingRef.current = !(videoRef.current?.paused ?? true);
      // videoRef.current?.pause();

      seekFromClientX(e.clientX, barEl);
    };

    const handleProgressPointerMove = (
      e: React.PointerEvent<HTMLDivElement>,
    ) => {
      if (isActionDisabled) return;
      if (!isScrubbingRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const barEl = e.currentTarget;

      if (rafSeekRef.current) cancelAnimationFrame(rafSeekRef.current);
      rafSeekRef.current = requestAnimationFrame(() => {
        seekFromClientX(e.clientX, barEl);
      });
    };

    const handleProgressPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
      if (isActionDisabled) return;
      if (!isScrubbingRef.current) return;
      e.preventDefault();
      e.stopPropagation();

      const barEl = e.currentTarget;
      try {
        if (pointerIdRef.current !== null) {
          barEl.releasePointerCapture(pointerIdRef.current);
        }
      } catch {}
      isScrubbingRef.current = false;

      if (rafSeekRef.current) {
        cancelAnimationFrame(rafSeekRef.current);
        rafSeekRef.current = null;
      }

      // If you paused during scrub and want to resume, uncomment:
      // if (wasPlayingRef.current) {
      //   videoRef.current?.play().catch(() => {});
      // }
    };

    const handleProgressPointerCancel = (
      e: React.PointerEvent<HTMLDivElement>,
    ) => {
      if (isActionDisabled) return;
      if (!isScrubbingRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      isScrubbingRef.current = false;
      if (rafSeekRef.current) {
        cancelAnimationFrame(rafSeekRef.current);
        rafSeekRef.current = null;
      }
    };

    const shouldAttemptSilentAutoUnlockForCurrentEpisode =
      isVisible &&
      !isUnlockingEpisode &&
      !autoUnlockAttemptedRef.current &&
      !!series?.seriesId &&
      canUnlockByCoins &&
      isEpisodeLockedForUser(video);

    const shouldOptimisticallyHideSeriesUnlockUiForAutoUnlock =
      shouldAttemptSilentAutoUnlockForCurrentEpisode &&
      Boolean(userData?.userId) &&
      Number.isFinite(episodeCoinCost) &&
      Number.isFinite(
        coinBalanceCache !== null ? coinBalanceCache : Number(coinBalance),
      ) &&
      Number(coinBalanceCache !== null ? coinBalanceCache : coinBalance) >=
        episodeCoinCost &&
      !hasLockedPreviousEpisode(video);

    const shouldShowUnlockProcessLoader = isVisible && isUnlockingEpisode;

    useEffect(() => {
      if (shouldShowUnlockProcessLoader) {
        if (unlockLoaderFadeTimerRef.current) {
          clearTimeout(unlockLoaderFadeTimerRef.current);
          unlockLoaderFadeTimerRef.current = null;
        }
        if (unlockLoaderRafRef.current !== null) {
          cancelAnimationFrame(unlockLoaderRafRef.current);
        }
        setShouldRenderUnlockLoader(true);
        unlockLoaderRafRef.current = requestAnimationFrame(() => {
          setIsUnlockLoaderVisible(true);
          unlockLoaderRafRef.current = null;
        });
        return;
      }
      setIsUnlockLoaderVisible(false);
      if (unlockLoaderFadeTimerRef.current) {
        clearTimeout(unlockLoaderFadeTimerRef.current);
      }
      unlockLoaderFadeTimerRef.current = setTimeout(() => {
        setShouldRenderUnlockLoader(false);
        unlockLoaderFadeTimerRef.current = null;
      }, 180);
    }, [shouldShowUnlockProcessLoader]);

    useEffect(
      () => () => {
        if (unlockLoaderRafRef.current !== null) {
          cancelAnimationFrame(unlockLoaderRafRef.current);
          unlockLoaderRafRef.current = null;
        }
        if (unlockLoaderFadeTimerRef.current) {
          clearTimeout(unlockLoaderFadeTimerRef.current);
          unlockLoaderFadeTimerRef.current = null;
        }
      },
      [],
    );

    const shouldRenderSeriesLockUi =
      isSeriesLocked &&
      !isAutoUnlockingVisibleEpisode &&
      !shouldOptimisticallyHideSeriesUnlockUiForAutoUnlock;

    return (
      <>
        <div className="player_item_wrapper">
          <div
            id={`content-video-${videoId}`}
            className={`player_item ${
              series && contentType === "series" ? "player_item_series" : ""
            } ${isPost ? "player_item-post" : ""} ${
              isSeriesLocked ? "player_item--locked" : ""
            } ${isPending ? "player_item--pending" : ""}`}
            ref={playerItemRef}
            style={playerStyle}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            {showPostMediaFrame ? (
              <div className="post-video-frame" style={postFrameStyle}>
                <div
                  className="post-video-blur"
                  style={
                    posterSrc
                      ? { backgroundImage: `url(${posterSrc})` }
                      : undefined
                  }
                ></div>
                <video
                  id={`video_player_${videoId}`}
                  ref={videoRef}
                  className={`video_player ${
                    isSeriesLocked ? "video_player--locked" : ""
                  }`}
                  data-stream-url={effectiveUrl || ""}
                  style={postVideoStyle}
                  src={effectiveUrl || ""}
                  poster={isSeriesLocked ? undefined : posterSrc}
                  controls={false}
                  muted={muted}
                  preload="metadata"
                  playsInline
                  autoPlay={isPlaying}
                  loop={!isSeriesContext}
                ></video>
              </div>
            ) : (
              <video
                id={`video_player_${videoId}`}
                ref={videoRef}
                className={`video_player ${
                  isSeriesLocked ? "video_player--locked" : ""
                }`}
                data-stream-url={effectiveUrl || ""}
                style={postVideoStyle}
                src={effectiveUrl || ""}
                poster={isSeriesLocked ? undefined : posterSrc}
                controls={false}
                muted={muted}
                preload="metadata"
                playsInline
                autoPlay={isPlaying}
                loop={!isSeriesContext}
              ></video>
            )}

            {shouldRenderUnlockLoader && (
              <div
                className="player-unlock-overlay"
                aria-live="polite"
                aria-busy="true"
                style={{
                  opacity: isUnlockLoaderVisible ? 1 : 0,
                  transition: "opacity 180ms ease",
                  pointerEvents: isUnlockLoaderVisible ? "auto" : "none",
                }}
              >
                <LoaderDark />
              </div>
            )}

            {isPending && (
              <div className="upload-pending-overlay--player">
                <div className="upload-pending-overlay__label">
                  {t("text-upload-in-progress")}
                </div>
              </div>
            )}

            {contentType === "series" && !isLockedPlaceholder && (
              <div className="episodesCount">
                {isCurrentPromoEpisode
                  ? t("label-promo")
                  : `${currentEpisodeCounter || 1} / ${totalEpisodeCounter}`}
              </div>
            )}

            {shouldRenderSeriesLockUi && (
              <>
                {showNoPurchaseOptionsLock && (
                  <div className="series-lock series-lock--no-options">
                    <div
                      className="series-lock__no-options-icon"
                      aria-hidden="true"
                    >
                      !
                    </div>
                    <h4>
                      {resolvePlayerLabel(
                        "label-no-purchase-options-available",
                        "No Purchase Options Available",
                      )}
                    </h4>
                    <p>
                      {resolvePlayerLabel(
                        "label-no-purchase-options-description",
                        "This episode can't be unlocked right now because purchase options aren't available yet.",
                      )}
                    </p>
                  </div>
                )}

                {showSubscriptionLock && (
                  <div
                    className={`series-unlock series-unlock--subscription ${
                      showSeriesUnlock ? "series-unlock--open" : ""
                    }`}
                  >
                    <div className="series-unlock__header">
                      <button
                        className="series-unlock-popup__close"
                        type="button"
                        onClick={handleSeriesUnlockClose}
                        disabled={!canSwapSeriesUnlockView}
                        aria-disabled={!canSwapSeriesUnlockView}
                      >
                        <CloseIcon />
                      </button>
                    </div>
                    <div className="series-unlock__subscription-content">
                      <h4>{t("label-access-now")}</h4>
                      {/* <p>{t("label-uninterrupted-access")}</p> */}
                      <button
                        className="series-unlock__cta"
                        onClick={handleCTARedirection}
                      >
                        <CTALock />
                        {getCTAText()}
                      </button>
                    </div>
                  </div>
                )}

                {showCoinUnlock && (
                  <div
                    className={`series-unlock ${
                      shouldUsePersonaBasedTwoStepUnlockUi
                        ? "series-unlock--persona-split"
                        : ""
                    } ${
                      shouldShowFreeUnlockView
                        ? "series-unlock--persona-split-free"
                        : ""
                    } ${showSeriesUnlock ? "series-unlock--open" : ""}`}
                  >
                    <div className="series-unlock__header">
                      <button
                        className="series-unlock-popup__close"
                        type="button"
                        onClick={handleSeriesUnlockClose}
                        disabled={!canSwapSeriesUnlockView}
                        aria-disabled={!canSwapSeriesUnlockView}
                      >
                        <CloseIcon />
                      </button>
                    </div>

                    {shouldUsePersonaBasedTwoStepUnlockUi ? (
                      <>
                        {shouldShowPurchaseUnlockView && (
                          <>
                            {shouldUsePersonaBasedSubscriptionAdsOnlyNoPackUi ? (
                              <div className="series-unlock__subscription-plan-card">
                                <h4>{subscriptionPlanUnlockTitle}</h4>
                                <button
                                  className="series-unlock__cta series-unlock__cta--persona"
                                  type="button"
                                  onClick={handleSeriesUnlockCtaClick}
                                >
                                  {subscriptionPlanCtaLabel}
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="series-unlock__panel series-unlock__panel--persona">
                                  <div className="series-unlock__panel-title series-unlock__panel-title--persona">
                                    <span>{unlockEpisodeTitle}</span>
                                    <CoinIcon
                                      className="gi-coin-icon"
                                      aria-hidden="true"
                                    />
                                    <span className="series-unlock__panel-amount">
                                      {episodeCoinCost}
                                    </span>
                                  </div>
                                  <div className="series-unlock__panel-balance">
                                    <span>{t("label-balance")}:</span>
                                    <CoinIcon
                                      className="gi-coin-icon gi-coin-small"
                                      aria-hidden="true"
                                    />
                                    <span>{coinBalance}</span>
                                  </div>
                                </div>

                                <div className="series-packs__container series-packs__container--persona">
                                  <h4>{buyCoinsLabel}</h4>
                                  <div className="series-unlock__packs series-unlock__packs--persona">
                                    {coinPackages.map((pack) => (
                                      <button
                                        key={pack.id}
                                        type="button"
                                        className="series-unlock__pack series-unlock__pack--persona"
                                        onClick={handleCoinPackClick}
                                      >
                                        <DynamicCoinPackIcon
                                          coinUrl={coinPackIconUrl}
                                        />
                                        <span className="series-unlock__pack-text">
                                          <span className="series-unlock__pack-amount">
                                            {pack.coins} {coinName}
                                          </span>
                                          <span className="series-unlock__pack-price">
                                            {getVisibleCoinPackPriceLabel(pack)}
                                          </span>
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {shouldShowSeriesUnlockCta && (
                                  <>
                                    <div className="series-unlock__divider series-unlock__divider--persona">
                                      <span>{t("label-or")}</span>
                                    </div>
                                    <button
                                      className="series-unlock__cta series-unlock__cta--persona"
                                      type="button"
                                      onClick={handleSeriesUnlockCtaClick}
                                    >
                                      {getCTAText()}
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </>
                        )}

                        {shouldShowFreeUnlockView && (
                          <>
                            {shouldShowPersonaCombinedFreeView && (
                              <>
                                <div className="series-unlock__persona-free-header">
                                  <h4 className="series-unlock__persona-free-title">
                                    {chooseUnlockTitle}
                                  </h4>
                                  <p className="series-unlock__persona-free-description">
                                    {chooseUnlockDescription}
                                  </p>
                                </div>

                                {showUnlockForFreeAdsOption && (
                                  <div className="series-unlock__free-section series-unlock__free-section--persona-ads">
                                    <p className="series-unlock-free__reward">
                                      {watchAdRewardLabel}
                                    </p>
                                    <button
                                      className="series-unlock-free__watch series-unlock-free__watch--persona"
                                      type="button"
                                      onClick={handleWatchAdClick}
                                    >
                                      <span className="series-unlock-free__watch-icon"></span>
                                      {watchAdCtaLabel}
                                    </button>
                                    <div className="series-unlock-free__progress">
                                      {splitAdsWatchedTodayLabel}
                                    </div>
                                  </div>
                                )}

                                {showUnlockForFreeAdsOption &&
                                  showEarnFromEngagementTip && (
                                    <div className="series-unlock__divider series-unlock__divider--persona">
                                      <span>{t("label-or")}</span>
                                    </div>
                                  )}

                                {showEarnFromEngagementTip && (
                                  <div className="series-unlock__free-section series-unlock__free-section--persona-engagement">
                                    <div className="series-unlock-free__tip series-unlock-free__tip--persona">
                                      <span className="series-unlock-free__tip-text">
                                        {earnCoinsTipText}
                                      </span>
                                      <button
                                        className="series-unlock-free__earn series-unlock-free__earn--persona"
                                        type="button"
                                        onClick={handleEarnCoinsNavigation}
                                      >
                                        {withCoinName(t("label-earn-coins"))}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {shouldShowPersonaEngagementOnlyView && (
                              <>
                                <div className="series-unlock__panel series-unlock__panel--persona series-unlock__panel--persona-free">
                                  <div className="series-unlock__panel-title series-unlock__panel-title--persona">
                                    <span>{unlockEpisodeTitle}</span>
                                    <CoinIcon
                                      className="gi-coin-icon"
                                      aria-hidden="true"
                                    />
                                    <span className="series-unlock__panel-amount">
                                      {episodeCoinCost}
                                    </span>
                                  </div>
                                  <div className="series-unlock__panel-balance">
                                    <span>{t("label-balance")}:</span>
                                    <CoinIcon
                                      className="gi-coin-icon gi-coin-small"
                                      aria-hidden="true"
                                    />
                                    <span>{coinBalance}</span>
                                  </div>
                                </div>

                                <div className="series-unlock__free-section series-unlock__free-section--persona-engagement-only">
                                  <div className="series-unlock-free__tip series-unlock-free__tip--persona-centered">
                                    <span className="series-unlock-free__tip-text">
                                      {earnCoinsTipText}
                                    </span>
                                    <button
                                      className="series-unlock-free__earn series-unlock-free__earn--persona-full"
                                      type="button"
                                      onClick={handleEarnCoinsNavigation}
                                    >
                                      {withCoinName(t("label-earn-coins"))}
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}

                            {shouldShowPersonaAdsOnlyView && (
                              <>
                                <div className="series-unlock__panel series-unlock__panel--persona series-unlock__panel--persona-free">
                                  <div className="series-unlock__panel-title series-unlock__panel-title--persona">
                                    <span>{unlockEpisodeTitle}</span>
                                    <CoinIcon
                                      className="gi-coin-icon"
                                      aria-hidden="true"
                                    />
                                    <span className="series-unlock__panel-amount">
                                      {episodeCoinCost}
                                    </span>
                                  </div>
                                  <div className="series-unlock__panel-balance">
                                    <span>{t("label-balance")}:</span>
                                    <CoinIcon
                                      className="gi-coin-icon gi-coin-small"
                                      aria-hidden="true"
                                    />
                                    <span>{coinBalance}</span>
                                  </div>
                                </div>

                                <div className="series-unlock__free-section series-unlock__free-section--persona-ads-only">
                                  <p className="series-unlock-free__reward">
                                    {watchAdRewardLabel}
                                  </p>
                                  <button
                                    className="series-unlock-free__watch series-unlock-free__watch--persona"
                                    type="button"
                                    onClick={handleWatchAdClick}
                                  >
                                    <span className="series-unlock-free__watch-icon"></span>
                                    {watchAdCtaLabel}
                                  </button>
                                  <div className="series-unlock-free__progress">
                                    {splitAdsWatchedTodayLabel}
                                  </div>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {!shouldHideCoinUnlockPanelForUnavailableCoinFlow && (
                          <div className="series-unlock__panel">
                            <div className="series-unlock__panel-title">
                              <span>{t("label-unlock-episode-in")}</span>
                              <CoinIcon
                                className="gi-coin-icon"
                                aria-hidden="true"
                              />
                              <span className="series-unlock__panel-amount">
                                {episodeCoinCost}
                              </span>
                            </div>
                            <div className="series-unlock__panel-balance">
                              <span>{t("label-balance")}:</span>
                              <CoinIcon
                                className="gi-coin-icon gi-coin-small"
                                aria-hidden="true"
                              />
                              <span>{coinBalance}</span>
                            </div>
                          </div>
                        )}

                        {Array.isArray(coinPackages) &&
                          coinPackages.length > 0 && (
                            <div className="series-packs__container">
                              <h4>Buy {coinName}</h4>
                              <div className="series-unlock__packs">
                                {coinPackages.map((pack) => (
                                  <button
                                    key={pack.id}
                                    type="button"
                                    className="series-unlock__pack"
                                    onClick={handleCoinPackClick}
                                  >
                                    <DynamicCoinPackIcon
                                      coinUrl={coinPackIconUrl}
                                    />
                                    <span className="series-unlock__pack-text">
                                      <span className="series-unlock__pack-amount">
                                        {pack.coins} {coinName}
                                      </span>
                                      <span className="series-unlock__pack-price series-unlock__pack-price--blur">
                                        {pack.priceLabel ||
                                          (Number.isFinite(Number(pack.price))
                                            ? `$${Number(pack.price).toFixed(
                                                2,
                                              )}`
                                            : "")}
                                      </span>
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                        {showUnlockForFreeOption && (
                          <div className="series-unlock__free-content">
                            {shouldShowAdsFreeSection && (
                              <div className="series-unlock__free-section series-unlock__free-section--ads">
                                <p className="series-unlock-free__reward">
                                  {withCoinName(
                                    t("label-watch-ad-earn-coins", {
                                      coins: watchAdRewardCoins,
                                    }),
                                  )}
                                </p>
                                <button
                                  className="series-unlock-free__watch"
                                  type="button"
                                  onClick={handleWatchAdClick}
                                  disabled
                                >
                                  <span className="series-unlock-free__watch-icon"></span>
                                  {t("label-watch-ad")}
                                </button>
                                <div className="series-unlock-free__progress">
                                  {adsWatchedTodayLabel}
                                </div>
                              </div>
                            )}
                            {shouldShowEngagementFreeSection && (
                              <div className="series-unlock__free-section series-unlock__free-section--engagement">
                                <div className="series-unlock-free__tip">
                                  <span className="series-unlock-free__tip-text">
                                    {withCoinName(t("label-earn-coins-tip"))}
                                  </span>
                                  <button
                                    className={`series-unlock-free__earn series-unlock-free__earn--full ${
                                      shouldUseOutlinedEarnButton
                                        ? "series-unlock-free__earn--outline"
                                        : ""
                                    }`}
                                    type="button"
                                    onClick={handleEarnCoinsNavigation}
                                  >
                                    {withCoinName(t("label-earn-coins"))}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {shouldShowSeriesUnlockCtaForCurrentSection && (
                          <>
                            {shouldShowSeriesUnlockDivider && (
                              <div className="series-unlock__divider">
                                <span>{t("label-or")}</span>
                              </div>
                            )}
                            <button
                              className="series-unlock__cta"
                              type="button"
                              onClick={handleSeriesUnlockCtaClick}
                            >
                              <CTALock />
                              {getCTAText()}
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {showMediaControls && (
              <>
                <PlayPauseButtons
                  className="play_button"
                  handlePlayAndPause={handlePlayAndPause}
                  isPlaying={videoIsPlaying}
                  showControls={showControls}
                />

                <div
                  className="video_controls"
                  style={{ opacity: showControls || !videoIsPlaying ? 1 : 0 }}
                  ref={videoControls}
                >
                  <PlayPauseButtons
                    className="play_pause_button"
                    handlePlayAndPause={handlePlayAndPause}
                    isPlaying={videoIsPlaying}
                    showControls={showControls}
                  />
                  <button
                    className="mute_unmute_button"
                    onClick={handleMuteUnmute}
                  >
                    {muted ? <Mute /> : <Unmute />}
                  </button>
                </div>
              </>
            )}

            <div className="user_info" ref={userInfoRef}>
              {repost && (
                <div className="repost_text" ref={repostRef}>
                  <button onClick={reDirectToOriginalVideo}>
                    <span>
                      <RepostIcon />
                    </span>
                    {t("label-text-reposted")}
                    <span className="dash">-</span>
                    {t(originalLabelKey)}
                  </button>
                </div>
              )}

              {!series && (
                <div
                  className={`top_info ${
                    showComments || showSeries || showShopping ? "pr_6" : ""
                  }`}
                  ref={topInfoRef}
                >
                  {userMetaData && userMetaData?.stories?.length > 0 ? (
                    <div className="img_name">
                      <Link
                        to={`/story-view/${userMetaData?.userId}?type=${PAGE.PROFILE}`}
                        onClick={(event) =>
                          handleStoryViewClick(
                            event,
                            `/story-view/${userMetaData?.userId}?type=${PAGE.PROFILE}`,
                          )
                        }
                      >
                        <img
                          src={profileImageUrl}
                          alt=""
                          style={{
                            border:
                              userMetaData?.stories?.length > 0
                                ? "2px solid #0033FF"
                                : "2px solid #fff",
                            padding:
                              userMetaData?.stories?.length > 0 ? "2px" : "0",
                          }}
                        />
                      </Link>
                      <Link
                        to={`/profile/${userId}`}
                        className="img_name"
                        onClick={blockIfDisabled}
                      >
                        <h6 className="user_name">
                          {fullName ? fullName : userName}
                        </h6>
                        {appConfig?.verifiedProfiles?.enabled &&
                          user?.verified && <VerifiedProfile />}
                      </Link>
                    </div>
                  ) : (
                    <a href={`/profile/${userId}`} onClick={blockIfDisabled}>
                      <div className="img_name">
                        <img
                          src={
                            profileImageUrl
                              ? profileImageUrl
                              : DefaultProfileImage
                          }
                          alt={`${fullName} profile pic`}
                        />
                        <h6 className="user_name">
                          {fullName ? fullName : userName}
                        </h6>
                        {appConfig?.verifiedProfiles?.enabled &&
                          user?.verified && <VerifiedProfile />}
                      </div>
                    </a>
                  )}

                  {userData?.userId !== userId && !isFollowing && (
                    <button
                      className="follow_btn"
                      disabled={isActionDisabled}
                      onClick={() =>
                        handleFollowEvent(video, user.userId, true)
                      }
                    >
                      <i className="fa fa-plus"></i>
                      {t("follow-btn")}
                    </button>
                  )}
                  {userData?.userId !== userId && isFollowing && (
                    <button
                      className="follow_btn"
                      disabled={isActionDisabled}
                      onClick={() =>
                        handleFollowEvent(video, user.userId, false)
                      }
                    >
                      {t("unfollow-btn")}
                    </button>
                  )}
                </div>
              )}

              <div
                className={`post_desc ${
                  showComments || showSeries || showShopping ? "pr_6" : ""
                }`}
                ref={postDescRef}
                onClickCapture={isActionDisabled ? blockIfDisabled : undefined}
                style={
                  isPost
                    ? {
                        maxHeight:
                          shouldShowMore || showMore
                            ? `${descTitleHeight + descMaxHeight}px`
                            : "none",
                        overflowY:
                          shouldShowMore || showMore
                            ? showMore
                              ? "auto"
                              : "hidden"
                            : "visible",
                      }
                    : undefined
                }
              >
                <h5 className="title">{displayTitle}</h5>
                <div
                  className={`desc
                    ${shouldShowMore ? "desc--has-toggle" : ""}
                    ${shouldShowMore && !showMore ? "desc--clamped" : ""}
                    ${showMore ? "desc--expanded" : ""}
                `}
                  ref={descRef}
                >
                  <span
                    className="desc_text"
                    ref={descTextRef}
                    style={
                      shouldShowMore && !showMore
                        ? {
                            maxHeight: `${descMaxHeight}px`,
                            overflow: "hidden",
                            display: "block",
                          }
                        : {
                            maxHeight: "none",
                            overflow: "visible",
                            display: "block",
                          }
                    }
                  >
                    <span
                      className="desc_text_content"
                      style={{ display: "inline" }}
                    >
                      {Array.isArray(descriptionContent)
                        ? descriptionContent.map((item) => item)
                        : typeof descriptionContent === "string"
                        ? descriptionContent
                        : null}
                    </span>

                    {shouldShowMore && (
                      <span className="desc-toggle">
                        {!showMore && (
                          <>
                            <span className="desc-ellipsis" aria-hidden="true">
                              …
                            </span>{" "}
                          </>
                        )}
                        <button
                          className="show-more-content"
                          onClick={() => {
                            if (isActionDisabled) return;
                            setShowMore((p) => !p);
                          }}
                          type="button"
                          disabled={isActionDisabled}
                        >
                          {showMore ? showLessLabel : showMoreLabel}
                        </button>
                      </span>
                    )}
                  </span>
                  <span
                    className="desc_text desc_text--measure"
                    ref={descMeasureRef}
                    aria-hidden="true"
                  >
                    {Array.isArray(parsedFullDescription)
                      ? parsedFullDescription.map((item) => item)
                      : typeof parsedFullDescription === "string"
                      ? parsedFullDescription
                      : null}
                  </span>
                </div>
              </div>
              {products?.length > 0 && (
                <div ref={productsRef}>
                  <VerticalPlayerProducts
                    show={true}
                    products={products || []}
                    disabled={isActionDisabled}
                    onShoppingSelect={(prod: any) => {
                      if (isActionDisabled) return;
                      setShowShopping(true);
                      setShowComments(false);
                      setShowSeries(false);
                      setShoppingProduct(prod);
                      setSelectedSize("regular");
                      if (videoRef.current) {
                        videoRef.current.classList.add("video-player-radius");
                        videoControls.current?.classList.add(
                          "video-player-radius",
                        );
                      }
                    }}
                  />
                </div>
              )}

              <div
                className={`transition-container ${showCTA ? "fade-in" : ""}`}
                ref={ctaRef}
              >
                {showCTA &&
                  series &&
                  typeof series === "object" &&
                  !Array.isArray(series) &&
                  !contentType && (
                    <div className="pl-5 pr-5 mt-4">
                      <button
                        className={`btn btn_primary w-[85%] m-0 md:w-full md:m-auto  ${
                          showComments || showSeries || showShopping
                            ? "btn_expand"
                            : ""
                        }`}
                        onClick={handleNavigateSeries}
                      >
                        {t("label-watch-series")}
                      </button>
                    </div>
                  )}
              </div>
              {videoPlaybackDuration > 0 && (
                <div
                  className="video-progress-container"
                  ref={progressRef}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progress)}
                  title={`${Math.round(progress)}%`}
                  style={{
                    cursor: "pointer",
                    // Helps touch drag without scrolling the page
                    touchAction: "none",
                    // Avoid text selection while scrubbing
                    userSelect: "none",
                  }}
                  onPointerDown={handleProgressPointerDown}
                  onPointerMove={handleProgressPointerMove}
                  onPointerUp={handleProgressPointerUp}
                  onPointerCancel={handleProgressPointerCancel}
                >
                  <div
                    className="video-progress-bar"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>

            {showRecommendations && (
              <Recommendations
                labels={seriesData?.current?.labels}
                onClose={() => setShowRecommendations(false)}
              />
            )}

            <div
              className={`post_icons ${
                products?.length > 0 && "post_icons_responsive"
              } ${
                showComments || showSeries || showShopping
                  ? `post_active post_icon_rtl ${
                      products?.length > 0 && "icons-transform"
                    }`
                  : ""
              } ${series ? "bottom-5" : ""}`}
              aria-disabled={isActionDisabled}
              data-disabled={isActionDisabled ? "true" : "false"}
            >
              {/* Like Icon  */}
              {(video?.likeEnabled || video?.likeEnabled === true) && (
                <button
                  className="like_icon"
                  title="I like this"
                  disabled={
                    isActionDisabled ||
                    isLikingVideo ||
                    (series?.paid && isCurrentEpisodeLockedForUser)
                  }
                  onClick={(e) => handleLikeEvent(e, video.videoId)}
                >
                  {isLikingVideo ? (
                    <LoaderDark />
                  ) : (
                    <div className="icon">
                      {isVideoLiked ? <LikeIcon /> : <UnLikeIcon />}
                    </div>
                  )}
                  <div
                    className={`icon-count like-count-${video.videoId}`}
                    id={`like-count-${video?.videoId}`}
                  >
                    {likeCount}
                  </div>
                </button>
              )}

              {/* Comment Icon  */}
              {(video?.commentEnabled || video?.commentEnabled === true) && (
                <button
                  className="comment_icon"
                  title="Comments"
                  onClick={() => {
                    if (isActionDisabled) return;
                    setShowComments((prev) => !prev);
                    setShowSeries(false);
                    setShowShopping(false);
                    if (videoRef.current) {
                      videoRef.current.classList.toggle("video-player-radius");
                      videoControls.current?.classList.toggle(
                        "video-player-radius",
                      );
                    }
                  }}
                  disabled={
                    isActionDisabled ||
                    (series?.paid && isCurrentEpisodeLockedForUser)
                  }
                >
                  <div className="icon">
                    <CommentIcon />
                  </div>
                  <div className="icon-count comment_count">
                    {safeCommentCount}
                  </div>
                </button>
              )}

              {/* Share Icon  */}
              {(video?.shareEnabled || video?.shareEnabled === true) && (
                <button
                  className="share_icon"
                  title="Share"
                  onClick={() => {
                    if (isActionDisabled) return;
                    setShowShare((prev) => !prev);
                  }}
                  disabled={
                    isActionDisabled ||
                    (series?.paid && isCurrentEpisodeLockedForUser)
                  }
                >
                  <div className="icon">
                    <ShareIcon />
                  </div>
                  <div className="icon-count share_count">
                    {shareCount || 0}
                  </div>
                </button>
              )}

              {series &&
                typeof series === "object" &&
                !Array.isArray(series) && (
                  <button
                    className="series_icon"
                    title="Series"
                    onClick={() => {
                      if (isActionDisabled) return;
                      setShowSeries((prev) => !prev);
                      setShowComments(false);
                      setShowShopping(false);
                      handleFetchSeries(series?.seriesId);
                      if (videoRef.current) {
                        videoRef.current.classList.toggle(
                          "video-player-radius",
                        );
                        videoControls.current?.classList.toggle(
                          "video-player-radius",
                        );
                      }
                    }}
                  >
                    <div className="icon">
                      <SeriesIcon />
                    </div>
                    <div className="icon-count">{t("label-series")}</div>
                  </button>
                )}

              {/* Repost Icon */}
              {((userData?.creator && appConfig?.inviteOnlyCreation) ||
                (!appConfig?.inviteOnlyCreation && appConfig?.ugcEnabled)) &&
                !series &&
                !isOwnContent && (
                  <button
                    className="repost_icon"
                    title="Repost"
                    onClick={() => {
                      if (isActionDisabled) return;
                      toggleRepostModal();
                    }}
                    disabled={isActionDisabled}
                  >
                    <div className="icon">
                      <RepostIcon />
                    </div>
                    <div className="icon-count repost_count">
                      {repostCount || 0}
                    </div>
                  </button>
                )}

              {false && (
                <button
                  className="shop_icon"
                  title="I dislike this"
                  onClick={async () => {
                    const isUserLoggedIn = await isLoggedin();
                    if (isUserLoggedIn) {
                      // setShowProducts((prev) => !prev);
                    } else {
                      setShowLoginModal(true);
                    }
                  }}
                >
                  <div className="icon">
                    <ShopIcon />
                  </div>
                </button>
              )}

              {!series && (
                <div className="misc_options_wrapper">
                  <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button
                      className="inline-flex w-full justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold text-gray-900"
                      disabled={isActionDisabled}
                    >
                      <OptionsIcon />
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute top-0 right-16 w-56 origin-top-left rounded-md focus:outline-none shadow-[0_0_32px_#00000014] cursor-pointer directionRtl">
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="#"
                              onClick={async (event) => {
                                event.preventDefault();
                                if (isActionDisabled) return;
                                const isUserLoggedIn = await isLoggedin();
                                if (isUserLoggedIn) {
                                  toggleReportModal();
                                } else {
                                  setShowLoginModal(true);
                                }
                              }}
                              className="reply_btn reply_actions p-2 block"
                            >
                              <div className="btn-wrap pb-2">
                                <span>
                                  <ReportIcon />
                                </span>
                                <p>{t("report")}</p>
                              </div>
                            </Link>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              )}

              {soundId && !series && (
                <div className="profile_thumbnail">
                  <button
                    onClick={() => {
                      if (isActionDisabled) return;
                      if (soundId) handleClickOnSound();
                    }}
                    disabled={isActionDisabled}
                  >
                    {soundThumbnail && (
                      <img
                        src={soundThumbnail && soundThumbnail}
                        alt={`${soundName}-thumbnail`}
                      />
                    )}

                    {!soundThumbnail && (
                      <>
                        <div className="icon">
                          <SoundWave />
                        </div>
                        <div></div>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div
            className={`video_expanded ${
              showComments || showSeries || showShopping
                ? "video_expanded_visible"
                : "video_expanded_hidden"
            }`}
          >
            {showComments && (
              <Comments
                video={video}
                setShowComments={setShowComments}
                videoRef={videoRef}
                videoControls={videoControls}
                setShowCommentReportModal={setShowCommentReportModal}
                setCommentPostId={setCommentPostId}
                commentCount={safeCommentCount}
                setCommentCount={setVideoCommentCount}
              />
            )}

            {showSeries && (
              <div className="episodes">
                <div className="top_content">
                  <h4 className="episodes-title">
                    {episodesCount} {t("label-episodes")}
                  </h4>
                  <button
                    className="close_button"
                    onClick={() => {
                      setShowSeries(false);
                      if (videoRef.current) {
                        videoRef?.current?.classList.remove(
                          "video-player-radius",
                        );
                        videoControls?.current?.classList.remove(
                          "video-player-radius",
                        );
                      }
                    }}
                  >
                    <CloseIcon />
                  </button>
                </div>

                {filteredEpisodes?.length === 0 &&
                seriesData?.current !== null ? (
                  <LoaderDark />
                ) : (
                  <ul className="episodes-list">
                    {filteredEpisodes?.map((item: any, index: any) => {
                      const isCurrentVideo = episodeKeysMatch(item, video);
                      const isPaid = isEpisodeLockedForUser(item);
                      const episodeLabel =
                        getEpisodeDisplayNumber(item) ??
                        filteredEpisodes
                          .slice(0, index + 1)
                          .filter((episode: any) => !isPromoEpisode(episode))
                          .length;
                      const isPromo = isPromoEpisode(item);

                      return (
                        <li
                          key={
                            item?.id ||
                            item?.videoId ||
                            item?.topicId ||
                            item?._id ||
                            `episode-${index}`
                          }
                          className={`episodes-list-item ${
                            isCurrentVideo && !isPaid ? "episodes-view" : ""
                          }`}
                          onClick={async () => {
                            if (isPaid) {
                              const lockedTarget =
                                getFirstLockedEpisodeTarget(item);
                              if (lockedTarget) {
                                await handlePaidEpisodeClick(
                                  lockedTarget.episode,
                                  lockedTarget.episodeLabel,
                                );
                                return;
                              }
                              await handlePaidEpisodeClick(item, episodeLabel);
                              return;
                            }
                            await handleFreeEpisodeClick(item, episodeLabel);
                          }}
                        >
                          {isPromo && !isCurrentVideo ? (
                            `${t("label-promo")}`
                          ) : isCurrentVideo && !isPaid ? (
                            <ViewIcon />
                          ) : isPaid ? (
                            <>
                              {episodeLabel}
                              <LockIcon />
                            </>
                          ) : (
                            <button>{episodeLabel}</button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {seriesData?.current === null && (
                  <div className="series-unavailable">
                    {t("label-series-not-available")}
                  </div>
                )}
              </div>
            )}

            {showShopping && (
              <div className="shopping_panel">
                <div className="shopping_header">
                  <button
                    className="shopping_close"
                    onClick={() => {
                      setShowShopping(false);
                      setShoppingProduct(null);
                      setSelectedSize("regular");
                      if (videoRef.current) {
                        videoRef?.current?.classList.remove(
                          "video-player-radius",
                        );
                        videoControls?.current?.classList.remove(
                          "video-player-radius",
                        );
                      }
                    }}
                  >
                    <CloseIcon />
                  </button>
                </div>
                <div className="shopping_body">
                  <div className="shopping_media">
                    {shoppingProduct?.imageUrl ||
                    shoppingProduct?.productImage ||
                    shoppingProduct?.thumbnailUrl ? (
                      <img
                        src={
                          shoppingProduct?.imageUrl ||
                          shoppingProduct?.productImage ||
                          shoppingProduct?.thumbnailUrl
                        }
                        alt={
                          shoppingProduct?.productName ||
                          t("label-product-image")
                        }
                      />
                    ) : (
                      <div className="shopping_media_placeholder"></div>
                    )}
                  </div>
                  <div className="shopping_content">
                    {(shoppingProduct?.productName ||
                      shoppingProduct?.name ||
                      shoppingProduct?.title) && (
                      <h4 className="shopping_title">
                        {shoppingProduct?.productName ||
                          shoppingProduct?.name ||
                          shoppingProduct?.title}
                      </h4>
                    )}
                    <p className="shopping_desc">
                      {t("label-shopping-description")}
                    </p>
                    <div className="shopping_size">
                      <p className="shopping_size_label">{t("label-size")}</p>
                      <div className="shopping_size_options">
                        <label
                          className={`shopping_size_option ${
                            selectedSize === "regular" ? "active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="shopping_size"
                            value="regular"
                            checked={selectedSize === "regular"}
                            onChange={() => setSelectedSize("regular")}
                          />
                          <span className="shopping_size_indicator" />
                          <span>{t("label-size-regular")}</span>
                        </label>
                        <label
                          className={`shopping_size_option ${
                            selectedSize === "large" ? "active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="shopping_size"
                            value="large"
                            checked={selectedSize === "large"}
                            onChange={() => setSelectedSize("large")}
                          />
                          <span className="shopping_size_indicator" />
                          <span>{t("label-size-large")}</span>
                        </label>
                      </div>
                    </div>
                    {shoppingProduct?.mrp && (
                      <div className="shopping_price">
                        ${shoppingProduct?.mrp}
                      </div>
                    )}
                    {(shoppingProduct?.shoppableLink ||
                      shoppingProduct?.productLink ||
                      shoppingProduct?.ctaUrl) && (
                      <>
                        {shoppingProduct?.targetExperience?.toLowerCase() ===
                        "shopping" ? (
                          <button
                            className="btn btn_primary w-full"
                            onClick={() => handleAddToCart(shoppingProduct)}
                            type="button"
                          >
                            {t("label-add-to-cart")}
                          </button>
                        ) : (
                          <a
                            className="btn btn_primary w-full"
                            href={
                              shoppingProduct?.shoppableLink ||
                              shoppingProduct?.productLink ||
                              shoppingProduct?.ctaUrl
                            }
                            onClick={async (event) => {
                              const isUserLoggedIn = await isLoggedin();
                              if (!isUserLoggedIn) {
                                event.preventDefault();
                                setShowLoginModal(true);
                              }
                            }}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t("label-add-to-cart")}
                          </a>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {showComments && <div className="mobile-backdrop"></div>}

          <RepostModal
            show={showRepost}
            onHide={toggleRepostModal}
            video={video}
          />

          <Share
            video={video}
            show={showShare}
            handleClose={() => setShowShare(false)}
          />

          {showReportModal && (
            <Report
              onHide={toggleReportModal}
              show={showReportModal}
              type="video"
              payloadData={{ userId, assetId: video.videoId }}
              handleClose={toggleReportModal}
            />
          )}

          {showCommentReportModal && (
            <Report
              show={showCommentReportModal}
              onHide={toggleCommentReportModal}
              type="comment"
              payloadData={{
                userId,
                assetId: video.videoId,
                postId: commentPostId,
              }}
              handleClose={toggleCommentReportModal}
              classes={"comment-report-modal"}
            />
          )}
        </div>

        {showSequenceUnlockModal && (
          <CustomModal
            isOpen={showSequenceUnlockModal}
            close={closeSequenceUnlockNotice}
            classes={"series-coin-modal"}
          >
            <div className="series-coin-modal__content">
              <h4 className="series-coin-modal__title">
                {t("label-episode-locked")}
              </h4>
              <p className="series-coin-modal__subtitle">
                {unlockNotice || sequenceUnlockMessage}
              </p>
            </div>
          </CustomModal>
        )}

        {showVideoError && (
          <CustomModal
            isOpen={showVideoError}
            close={() => setShowVideoError(false)}
          >
            <h2>{videoErrorMessage}</h2>
          </CustomModal>
        )}

        {showCoinModal && (
          <CustomModal
            isOpen={showCoinModal}
            close={() => setShowCoinModal(false)}
            classes={"series-coin-modal"}
          >
            <div className="series-coin-modal__content">
              <div className="series-coin-modal__icon" aria-hidden="true">
                <SeriesCoinModalIcon />
              </div>
              <h4 className="series-coin-modal__title">
                {t("label-download-our-app")}
              </h4>
              {shouldShowInAppPurchaseUnsupportedNotice && (
                <p className="series-coin-modal__web-note">
                  {t("label-in-app-purchase-web-not-supported")}
                </p>
              )}
              <p className="series-coin-modal__subtitle">
                {t("label-download-our-app-subtitle")}
              </p>
              <div className="series-coin-modal__divider" />
              <p className="series-coin-modal__detail">
                {t("label-feature-available-app-detail")}
              </p>
              <div className="series-coin-modal__stores">
                <Link
                  to={
                    "https://apps.apple.com/in/app/gluedin-video-community/id1622464243"
                  }
                  target="_blank"
                  className="series-coin-modal__store-button"
                >
                  <AppStoreBadgeIcon />
                </Link>
                <Link
                  to={
                    "https://play.google.com/store/apps/details?id=com.gluedin.app"
                  }
                  target="_blank"
                  className="series-coin-modal__store-button"
                >
                  <GooglePlayBadgeIcon />
                </Link>
              </div>
            </div>
          </CustomModal>
        )}

        {seriesNotAvailable && (
          <CustomModal
            isOpen={seriesNotAvailable}
            close={() => setSeriesNotAvailable(false)}
            classes={"series-popup"}
          >
            <h4>{t("label-series-not-available")}</h4>
            <p>{t("label-series-removed")}</p>
            <button onClick={() => setSeriesNotAvailable(false)}>
              {t("label-close")}
            </button>
          </CustomModal>
        )}
      </>
    );
  },
);

export default VideoItem;
